import React, {
  useCallback,
  useState,
  useMemo,
  useEffect,
  useRef,
} from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Avatar,
  Checkbox,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Stack,
  Slide,
  Button,
  Link,
  Backdrop,
  ShareIcon,
  AddIcon,
} from "../../../ui";
import {
  InfoOutlined as InfoIcon,
  History as HistoryIcon,
  Edit as EditIcon,
  Description as DescriptionIcon,
  ArrowForward as ArrowForwardIcon,
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  AccountBalance as AccountBalanceIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
  AccessTimeFilled as AccessTimeFilledIcon,
} from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import ContactPreferenceCard, { FeeLine } from "@/components/share/ContactPreferenceCard";
import { PAYPLAN_ALREADY_SHARED_TEXT, PAYPLAN_COMPANY_NAME, SHARE_FEE_LINES } from "@/components/payplan/payplan.config";
import { cleanPhoneNumber, isValidPhoneNumber } from "@/shared/lib/utils/phoneNumber";
import {
  type CallTime,
  type ContactDetails,
  contactDetailsFromRow,
  contactDetailsFromChoice,
  choiceIsNewer,
  callTimeMissing,
  payPlanNotifyFields,
  readContactChoices,
  saveContactChoice,
  preferenceFields,
} from "@/shared/share/preferencePayload";
import { useUI } from "@/shared/state/store";
import { SharePageService } from "@/shared/api/services/sharePageService";

import AdditionalSupportModal from "./AdditionalSupportModal";
import EditCompanyDialog from "@/components/EditCompanyDialog";
import DeleteCompanyDialog from "@/components/DeleteCompanyDialog";

import {
  ApiResponse,
  ApplianceData,
  CalculatedTotals,
  CustomerDeclarationEntry,
  CustomerLatestSnapshot,
  CustomerPreferencesEntry,
  CustomerProfileData,
  CustomerSharingResponse,
  DeclarationAllEntry,
  DigitalLender,
  NotifyCustomerDebtAdvice,
  NotifyCustomerDebtAdviceResponse,
  SendEmailOnShareEntry,
  ServiceResponse,
  SharedDocResponse,
} from "@/shared/api/types";
import { httpGet, httpPatch, httpPost, httpPut } from "@/shared/api/http";
import {
  customerEndpoints,
  debtEndpoints,
  declarationEndpoints,
  documentsPageEndpoints,
  latestUpdateEndpoints,
  onboardingEndpoints,
  sharePageEndpoints,
} from "@/shared/api/endpoints";
import femaCapture from "@/shared/fema/capture";
import AddCompanyDialog from "@/components/AddCompanyDialog";
import ShareHistoryDialog from "@/components/ShareHistoryDialog";
import UUDeclarationDialog from "@/components/UUDeclarationDialog";
import YWCTDeclarationDialog from "@/components/YWCTDeclarationDialog";
import CadentGasDialog from "@/components/CadentGasDialog";

import {
  DocumentsPageService,
  LenderService,
  OnboardingService,
  DashboardService,
} from "@/shared/api/services";
import { ActionType } from "@/shared/api/types";
import { findMasterLender, LenderParentRecord } from "@/shared/api/services/lenderService";
import { formatDate } from "@/shared/lib/utils/formatDate";
import { getCustomerId } from "@/shared/lib/utils/getCustomerId";
import { Checkbox as CheckboxMui, CircularProgress } from "@mui/material";
import { useAlert } from "@/app/providers/AlertProvider";
import { ThemeColours } from "@/clients/acme/config/colours";
import InfoDialog from "@/components/InfoDialog";
import placeholderIllustration from "@/assets/ai-answers.svg";
import { trackMetaEvent } from "@/shared/tracking/metaTracking";
import { isPartialNameMatch } from "@/shared/lib/utils/freepostMatcher";
import {
  getCompanyDocs,
  isSameDay,
  sortBySourceAndCreationDate,
} from "@/shared/share/sharePageUtils";
import {
  CORE_ASP_COMPANIES,
  isCoreAspCompany,
  isEligibleAspCondition,
} from "@/constants/asp";

// ==================== Type Definitions ====================

export interface Document {
  secondId?: string;
  id: string;
  name: string;
  size: string;
  uploadedDate: string;
  type: "wage-slip" | "bank-statement" | "other";
  required?: boolean;
  raw?: SharedDocResponse;
  isNewlyUploaded?: boolean;
  formData?: FormData;
  description?: string;
  documentId?: string | number;
}

export interface NdlData {
  preferredTimeForNdl?: string;
  debtAdviceContactPreferenceNdl?: string;
  voicemailConsentNdl?: boolean;
  emailMeNdl?: boolean;
  textMeNdl?: boolean;
  customerPrefrences?: any;
  isPreferencesLoaded?: boolean;
}

export interface PaymentOfferData {
  paymentOfferAmount: string | number;
  paymentPreferredFrequency: string | number;
}

type DeclarationStatusType = "not-completed" | "accepted" | "rejected";

export interface Company {
  notSupported?: boolean;
  declarationData: CustomerDeclarationEntry | DeclarationAllEntry | undefined;
  id: string;
  name: string;
  reference: string;
  type:
    | "referred"
    | "debt"
    | "additional-support"
    | "referred-linked"
    | "added"
    | "all";
  documents: Document[];
  selectedDocuments: string[];
  lastShared?: string;
  address?: string;
  raw?: CustomerSharingResponse;
  isFp?: boolean;
  declarationStatus?: DeclarationStatusType;
  ndlData?: NdlData;
  paymentOfferData?: PaymentOfferData;
  applianceCount?: number;
  disableShare?: boolean;
  agentSubmitted?: boolean;
  isPayPlanShared?: boolean;
}

interface HouseholdDeclaration {
  status: "yes" | "no";
  circumstances: "yes" | "no";
  changeInCircumstances?: string;
  negativeCircumstanceChange?: string;
  previousCredit: "yes" | "no";
  financialPositionChange: "yes" | "no" | "not-applicable";
}

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  uploadDate: Date;
  file: File;
}

type TabType =
  | "all"
  | "referred"
  | "referred-linked"
  | "debts"
  | "additional-support"
  | "added";

// ==================== Component ====================

const Transition = React.forwardRef<
  unknown,
  { in?: boolean; children: React.ReactElement }
>(function Transition(props, ref) {
  return <Slide direction="up" ref={ref as React.Ref<unknown>} {...props} />;
});

// Mobile and email from the cached customer record (empty when missing or unreadable).
function readCustomerContact(): { mobile: string; email: string } {
  try {
    const stored = JSON.parse(localStorage.getItem("customerData") || "{}");
    return { mobile: stored?.mobileNumber ?? "", email: stored?.emailAddress ?? "" };
  } catch {
    return { mobile: "", email: "" };
  }
}

// Companies that use the shared contact card.
const CONTACT_COMPANIES = ["PayPlan", "National Debtline"];

// Company cards render in both the mobile and desktop trees with the same id; scroll to the one on screen.
const scrollToCompanyCard = (companyId: string | number): void => {
  const cards = document.querySelectorAll<HTMLElement>(`[id="share-company-card-${companyId}"]`);
  const visibleCard = Array.from(cards).find((card) => card.getClientRects().length > 0);
  visibleCard?.scrollIntoView({ behavior: "smooth", block: "center" });
};

// Starting contact details: the remembered choice, else phone (WhatsApp was default on for PayPlan).
function defaultContacts(profile: { mobile: string; email: string }): Record<string, ContactDetails | undefined> {
  const choices = readContactChoices();
  const base = (_name: string): ContactDetails => ({ method: "phone", mobile: profile.mobile, email: profile.email, whatsApp: false, callTime: "Anytime" });
  return Object.fromEntries(
    CONTACT_COMPANIES.map((name) => [
      name,
      contactDetailsFromChoice(choices[name], profile) ?? (isValidPhoneNumber(profile.mobile) ? base(name) : undefined),
    ]),
  );
}

const SharePage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { addAlert } = useUI();
  const { showAlert } = useAlert();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAdditionalSupportOpen, setIsAdditionalSupportOpen] =
    useState<boolean>(() => {
      // Default open unless explicitly disabled via query
      return searchParams.get("showAdditionalSupport") !== "false";
    });
  const [eligibleAspConditions, setEligibleAspConditions] = useState<
    string[] | null
  >(null);
  const [showUpdatedRecommendationsModal, setShowUpdatedRecommendationsModal] =
    useState(false);
  const [ineligibleCompanies, setIneligibleCompanies] = useState<string[]>([]);
  const [
    updatedRecommendationsModalSource,
    setUpdatedRecommendationsModalSource,
  ] = useState<"history" | "share_securely">("share_securely");
  const [hasMobileNumber, setHasMobileNumber] = useState(false);
  // Mobile and email from the customer record, shared by the PayPlan and NDL cards
  const [profileContact, setProfileContact] = useState(readCustomerContact);
  // Set once a card save changes the profile, so a late profile load can't overwrite it with an older value.
  const contactSavedRef = useRef(false);
  // Contact details per company (PayPlan, NDL): pre-saved when the profile has a mobile number, otherwise filled on the card.
  const [contactDetails, setContactDetails] = useState<Record<string, ContactDetails | undefined>>(() =>
    defaultContacts(readCustomerContact()),
  );
  const [contactErrors, setContactErrors] = useState<Record<string, boolean>>({});
  const [contactEditing, setContactEditing] = useState<Record<string, boolean>>({});
  const [isSharing, setIsSharing] = useState(false);
  const [debts, setDebts] = useState<[]>([]);

  const [emailAddress, setEmailAddress] = useState("");

  const [companyLastSharedDate, setCompanyLastSharedDate] = useState<number>(0);
  const [docsCreationDate, setDocsCreationDate] = useState<number>(0);
  const [debtsCreationDate, setDebtsCreationDate] = useState<number>(0);
  const [latestIeUpdateDate, setLatestIeUpdateDate] = useState<number>(0);
  const latestIeUpdateDateRef = useRef<number>(0);

  // Legacy parity: fire Meta Pixel + CAPI event when Share screen is opened
  useEffect(() => {
    void trackMetaEvent("InitiateCheckout", "InitiateCheckout");
  }, []);

  const [companyShareLastUpdateDate, setCompanyShareLastUpdateDate] =
    useState<number>(0);
  const [freePostLastUpdateDate, setFreePostLastUpdateDate] =
    useState<number>(0);

  const [freePostLastSharedDate, setFreePostLastSharedDate] =
    useState<number>(0);

  const [freepostCreditorNamesData, setFreepostCreditorNamesData] = useState(
    [],
  );

    const [loadedFreepostCreditorNamesData, setLoadedFreepostCreditorNamesData] = useState(false);


  const [cadentData, setCadentData] = useState([]);

  const [latestSnapshot, setLatestSnapshot] = useState<CustomerLatestSnapshot>({
    creationDate: "",
    lastUpdateDate: "",
    createdBy: 0,
    lastUpdatedBy: 0,
    id: 0,
    customerId: 0,
    balanceAmount: 0,
  });

  const [summary, setSummary] = useState<CalculatedTotals>({
    customerId: 0,
    totalIncome: 0,
    totalAssets: 0,
    totalOutgoingsFixed: 0,
    totalOutgoingsFlexible: 0,
    totalOwingOnPriorityDebt: 0,
    totalRepayOnPriorityDebt: 0,
    totalOwingOnNonPriorityDebt: 0,
    totalRepayOnNonPriorityDebt: 0,
    pendingPaymentOffer: 0,
    balanceAmount: 0,
    totalRepaymentDebts: 0,
    totalOwingOnDebts: 0,
  });

  const [isSummaryLoaded, setIsSummaryLoaded] = useState(false);
  const [hasCompaniesLoaded, setHasCompaniesLoaded] = useState(false);

  const [declarationData, setDeclarationData] = useState<
    CustomerDeclarationEntry[]
  >([]);
  const [declarationAllData, setDeclarationAllData] = useState<
    DeclarationAllEntry[]
  >([]);

  const [digitalLenders, setDigitalLenders] = useState<DigitalLender[]>([
    {
      schemaName: "",
      creditor: "",
      subDomain: "",
      lender: "",
      referenceNumberFormat: "",
      customerPaymentOffer: "",
      showCompanyList: "",
      addToDebtList: "",
      parentCompany: "",
      creditorAddress: "",
      isCreditorExistsWithIeHub: "",
    },
  ]);

  // Page-state setters for a company's NDL preferences; they do not call the backend.
  const setCustomerPrefrences = (
    sharingId: string | number,
    obj: Record<string, any>,
  ) => {
    setCompanies((prev: any) =>
      prev.map((company: any) =>
        company.id === sharingId
          ? {
              ...company,
              ndlData: {
                ...company.ndlData,
                customerPrefrences: obj,
              },
            }
          : company,
      ),
    );
  };

  const setIsPreferencesLoaded = (
    sharingId: string | number,
    choice: boolean,
  ) => {
    setCompanies((prev: any) =>
      prev.map((company: any) =>
        company.id === sharingId
          ? {
              ...company,
              ndlData: {
                ...company.ndlData,
                isPreferencesLoaded: choice,
              },
            }
          : company,
      ),
    );
  };

  const setPreferredTimeForNdl = (sharingId: string | number, data: string) => {
    setCompanies((prev: any) =>
      prev.map((company: any) =>
        company.id === sharingId
          ? {
              ...company,
              ndlData: {
                ...company.ndlData,
                preferredTimeForNdl: data,
              },
            }
          : company,
      ),
    );
  };

  const setDebtAdviceContactPreferenceNdl = (
    sharingId: string | number,
    data: string,
  ) => {
    setCompanies((prev: any) =>
      prev.map((company: any) =>
        company.id === sharingId
          ? {
              ...company,
              ndlData: {
                ...company.ndlData,
                debtAdviceContactPreferenceNdl: data,
              },
            }
          : company,
      ),
    );
  };

  const setVoicemailConsentNdl = (
    sharingId: string | number,
    data: boolean,
  ) => {
    setCompanies((prev: any) =>
      prev.map((company: any) =>
        company.id === sharingId
          ? {
              ...company,
              ndlData: {
                ...company.ndlData,
                voicemailConsentNdl: data,
              },
            }
          : company,
      ),
    );
  };

  const setEmailMeNdl = (sharingId: string | number, choice: boolean) => {
    setCompanies((prev: any) =>
      prev.map((company: any) => {
        return company.id === parseInt(sharingId)
          ? {
              ...company,
              ndlData: {
                ...company.ndlData,
                emailMeNdl: choice,
              },
            }
          : company;
      }),
    );
  };

  const setTextMeNdl = (sharingId: string | number, choice: boolean) => {
    setCompanies((prev: any) =>
      prev.map((company: any) =>
        company.id === sharingId
          ? {
              ...company,
              ndlData: {
                ...company.ndlData,
                textMeNdl: choice,
              },
            }
          : company,
      ),
    );
  };

  const [ieMstrLenderData, setIeMstrLenderData] = useState<
    LenderParentRecord[]
  >([
    {
      id: 0,
      parentCompany: "",
      description: "",
      showCompanyList: false,
      referralCompanies: false,
      customerPaymentOffer: false,
      addToDebtList: false,
      addToShareListFromProfile: false,
      referenceNumberFormat: "",
      childCompany: [],
      raw: { id: 0, lender: "" },
      childrenRaw: [],
    },
  ]);

  const [ieMstrLenderDataLoaded, setIeMstrLenderDataLoaded] = useState(false);

  // Section refs for scrolling
  const allSectionRef = useRef<HTMLDivElement>(null);
  const referredSectionRef = useRef<HTMLDivElement>(null);
  const debtsSectionRef = useRef<HTMLDivElement>(null);
  const additionalSupportSectionRef = useRef<HTMLDivElement>(null);
  const addedSectionRef = useRef<HTMLDivElement>(null);

  // ==================== State Management ====================
  const [activeTab, setActiveTab] = useState<TabType>("debts");
  const [digitalData, setDigitalData] = useState<ApiResponse<any[]>>();

  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openInfoDialog, setOpenInfoDialog] = useState(false);

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const [openUUDeclarationDialog, setOpenUUDeclarationDialog] = useState(false);
  const [openYWCTDeclarationDialog, setOpenYWCTDeclarationDialog] =
    useState(false);
  const [openCadentGasDialog, setOpenCadentGasDialog] = useState(false);

  const [declarationCompany, setDeclarationCompany] = useState<Company>({
    id: "",
    name: "",
    reference: "",
    type: "debt",
    documents: [],
    selectedDocuments: [],
  });

  const [selectedEditCompany, setSelectedEditCompany] = useState<Company>({
    id: "",
    name: "",
    reference: "",
    type: "debt",
    documents: [],
    selectedDocuments: [],
  });

  const [companies, setCompanies] = useState<Company[]>([]);

  useEffect(() => {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    if (customerData.mobileNumber) {
      setHasMobileNumber(true);
    }
  }, []);

  // GET Cadent Gas Data
  useEffect(() => {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    async function getCadentGasData() {
      try {
        const response = await httpGet<ApplianceData[]>(
          sharePageEndpoints.getCadentAdditionalQuestions(
            customerData.customerId,
          ),
        );

        const res: any = response?.data;

        setCadentData(res);
      } catch (error) {
        femaCapture("fetching_cadent_gas_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        throw error;
      }
    }

    getCadentGasData();
  }, []);

  // SET email address
  useEffect(() => {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    setEmailAddress(customerData.emailAddress);
  }, []);

  // Fetch documents
  useEffect(() => {
    const fetchDocuments = async (): Promise<ServiceResponse<any[]>> => {
      let resolvedCustomerId: string | number | null = await getCustomerId();

      if (!resolvedCustomerId) {
        addAlert({
          severity: "warning",
          message:
            "Missing customerId. Add ?customerId= in URL or ensure you are logged in.",
          autoHide: true,
          duration: 5000,
        });
        return {
          data: [],
          success: false,
          timestamp: new Date().toISOString(),
        };
      }

      try {
        const data =
          await DocumentsPageService.getCustomerDocuments(resolvedCustomerId);

        return data;
      } catch (error) {
        console.error("Failed to fetch companies", error);
        return {
          data: [],
          success: false,
          timestamp: new Date().toISOString(),
        };
      }
    };

    fetchDocuments().then(async (documentsArrayPromise) => {
      const documentsArray = await documentsArrayPromise;

      if (documentsArray && Array.isArray(documentsArray)) {
        for (const doc of documentsArray) {
          let docsCreationDate = 0;

          docsCreationDate = Math.max(
            docsCreationDate,
            new Date(doc.creationDate).getTime(),
          );

          setDocsCreationDate(docsCreationDate);
        }
      }
    });
  }, []);

  const hasFetchedDebts = useRef(false);

  // GET DEBTS
  useEffect(() => {
    if (hasFetchedDebts.current) return;
    hasFetchedDebts.current = true;

    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    async function getDebts() {
      const debts = await OnboardingService.getDebts(
        Number(customerData.customerId),
        {
          skipErrorCapture: true,
        },
      );
      return debts;
    }

    getDebts().then((debts) => {
      let debtsCreationDate = 0;

      for (const debt of debts) {
        if (!debt.creationDate && !debt.lastUpdateDate) continue;

        debtsCreationDate = Math.max(
          debtsCreationDate,
          new Date(debt.creationDate || debt.lastUpdateDate).getTime(),
        );
      }

      setDebtsCreationDate(debtsCreationDate);
      setDebts(debts);
    });
  }, []);

  // GET declaration data all
  useEffect(() => {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    async function getDeclarationDataAll() {
      try {
        const response = await httpGet<ApiResponse>(
          declarationEndpoints.getDeclarationAll(customerData.customerId),
        );

        const data: any = response?.data;

        setDeclarationAllData(data);
      } catch (error) {
        femaCapture("fetching_declaration_all_data_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        throw error;
      }
    }

    getDeclarationDataAll();
  }, []);

  // GET Freepost Creditor List Names
  // Lift state for add company dialog
  useEffect(() => {
    async function getFreepostCreditorNames(): Promise<ServiceResponse<any[]>> {
      try {
        const response = await httpGet<ApiResponse>(
          customerEndpoints.listFreepostLenders,
        );
        return response.data;
      } catch (error) {
        femaCapture("fetching_freepost_creditor_list_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        throw error;
      }
    }

    getFreepostCreditorNames().then((data: any) => {
      setFreepostCreditorNamesData(data);
      setLoadedFreepostCreditorNamesData(true);
    });
  }, []);

  const hasFetchedSummary = useRef(false);

  // GET summary
  useEffect(() => {
    if (hasFetchedSummary.current) return;
    hasFetchedSummary.current = true;

    async function getSummary(): Promise<ServiceResponse<any[]>> {
      try {
        const resolvedCustomerId: string | number | null =
          await getCustomerId();

        const response = await httpGet<ApiResponse>(
          customerEndpoints.getCalculatedTotals(Number(resolvedCustomerId)),
        );
        return response.data;
      } catch (error) {
        femaCapture("fetching_calculated_totals_data_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        throw error;
      }
    }

    getSummary().then((res: any) => {
      setSummary(res[0]);
      setIsSummaryLoaded(true);
    });
  }, []);

  // GET latest snapshot
  useEffect(() => {
    async function getLatestSnapshot(): Promise<ServiceResponse<any[]>> {
      try {
        const resolvedCustomerId: string | number | null =
          await getCustomerId();

        const response = await httpGet<ApiResponse>(
          sharePageEndpoints.getCustomerLatestSnapshot(
            Number(resolvedCustomerId),
          ),
        );
        return response.data;
      } catch (error) {
        femaCapture("fetching_latest-snapshot_data_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        throw error;
      }
    }

    getLatestSnapshot().then((res: any) => {
      setLatestSnapshot(res[0]);
    });
  }, []);

  // GET declaration data
  useEffect(() => {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    async function getDeclarationData() {
      try {
        const response = await httpGet<ApiResponse>(
          declarationEndpoints.getDeclaration(customerData.customerId),
        );

        const data: any = response?.data;

        setDeclarationData(data);
        setHasLoadedDeclarationData(true);
      } catch (error) {
        femaCapture("fetching_declaration_data_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        throw error;
      }
    }

    getDeclarationData();
  }, []);

  const hasFetchedCompanies = useRef(false);

  // FETCH companies data
  useEffect(() => {
    if (
      hasFetchedCompanies.current ||
      !hasLoadedDeclarationData ||
      !debts ||
      !isSummaryLoaded ||
      !ieMstrLenderDataLoaded || 
      !loadedFreepostCreditorNamesData
    )
      return;

    hasFetchedCompanies.current = true;

    const fetchCompanies = async (): Promise<Company[]> => {
      // Fetch the latest IE update date first so latestIeUpdateDateRef.current
      // is populated before companyShouldBeEnabled is evaluated below.
      await getLatestIeUpdateDate();
      const resolvedCustomerId: string | number | null = await getCustomerId();

      if (!resolvedCustomerId) {
        addAlert({
          severity: "warning",
          message:
            "Missing customerId. Add ?customerId= in URL or ensure you are logged in.",
          autoHide: true,
          duration: 5000,
        });
        return [];
      }

      try {
        const coerceNumericId = (value: unknown): number | null => {
          if (typeof value === "number" && Number.isFinite(value)) return value;
          if (typeof value === "string") {
            const trimmed = value.trim();
            if (!trimmed) return null;
            const parsed = Number(trimmed);
            return Number.isFinite(parsed) ? parsed : null;
          }
          return null;
        };

        const isSharedRow = (row: any): boolean => {
          const submit =
            typeof row?.submitDate === "string" ? row.submitDate.trim() : "";
          const lastShared =
            typeof row?.lastSharedDate === "string"
              ? row.lastSharedDate.trim()
              : "";
          return Boolean(submit || lastShared);
        };

        /**
         * IMPORTANT (legacy parity):
         * - Keep ALL rows that were already shared (submitDate/lastSharedDate present),
         *   because history must remain visible (and reference edits after sharing create a new row).
         * - Only dedupe *pending/unshared* duplicates (no submitDate/lastSharedDate), since these
         *   are the "company shows twice" bug scenario.
         */
        const dedupeSharingRows = (rows: any[]): any[] => {
          const bestPendingByKey = new Map<string, any>();

          const keyForRow = (row: any): string => {
            const debtId = coerceNumericId(row?.debtId);
            const id = coerceNumericId(row?.id);
            if (typeof debtId === "number") return `debt:${debtId}`;
            if (typeof id === "number") return `id:${id}`;
            return `fallback:${String(row?.lenderName ?? row?.companyName ?? "")}|${String(
              row?.accountNumber ?? row?.referenceNumber ?? "",
            )}`;
          };

          // Pass 1: choose the "best" pending row per key (latest lastUpdateDate)
          for (const row of rows) {
            if (isSharedRow(row)) continue;
            const key = keyForRow(row);
            const prev = bestPendingByKey.get(key);
            const prevTs = prev?.lastUpdateDate
              ? Date.parse(prev.lastUpdateDate)
              : NaN;
            const nextTs = row?.lastUpdateDate
              ? Date.parse(row.lastUpdateDate)
              : NaN;
            if (
              !prev ||
              (Number.isFinite(nextTs) &&
                (!Number.isFinite(prevTs) || nextTs >= prevTs))
            ) {
              bestPendingByKey.set(key, row);
            }
          }

          // Pass 2: rebuild list preserving original order:
          // - include all shared rows
          // - include only the chosen pending row per key
          const emittedPendingKeys = new Set<string>();
          const out: any[] = [];
          for (const row of rows) {
            if (isSharedRow(row)) {
              out.push(row);
              continue;
            }
            const key = keyForRow(row);
            if (emittedPendingKeys.has(key)) continue;
            if (bestPendingByKey.get(key) === row) {
              out.push(row);
              emittedPendingKeys.add(key);
            }
          }
          return out;
        };

        const digitalDataRaw: any =
          await SharePageService.getCustomerSharing(resolvedCustomerId);
        const digitalData: any[] = Array.isArray(digitalDataRaw)
          ? dedupeSharingRows(digitalDataRaw)
          : [];

        let localCompanyShareLastUpdateDate = 0;

        let companyLastSharedDate = null;

        for (const company of digitalData) {
          localCompanyShareLastUpdateDate = Math.max(
            localCompanyShareLastUpdateDate,
            new Date(company.lastUpdateDate).getTime(),
          );

          setCompanyShareLastUpdateDate(localCompanyShareLastUpdateDate);

          const updateTime = new Date(company.lastUpdateDate).getTime();
          companyLastSharedDate =
            companyLastSharedDate === null
              ? updateTime
              : Math.max(companyLastSharedDate, updateTime);
        }

        setCompanyLastSharedDate(companyLastSharedDate as number);

        setDigitalData(digitalData);

        const customerData = JSON.parse(
          localStorage.getItem("customerData") as string,
        );

        const freePostDataRaw: any = await SharePageService.getFreePostData(
          customerData.customerId,
        );
        const freePostData: any[] = Array.isArray(freePostDataRaw)
          ? dedupeSharingRows(freePostDataRaw)
          : [];

        let freePostLastSharedDate = null;

        for (const company of freePostData) {
          let freePostLastUpdateDate = 0;

          freePostLastUpdateDate = Math.max(
            freePostLastUpdateDate,
            new Date(company.lastUpdateDate).getTime(),
          );

          setFreePostLastUpdateDate(freePostLastUpdateDate);

          const updateTime = new Date(company.lastUpdateDate).getTime();

          freePostLastSharedDate =
            freePostLastSharedDate === null
              ? updateTime
              : Math.max(freePostLastSharedDate, updateTime);
        }

        setFreePostLastSharedDate(freePostLastSharedDate as number);
        
        const data = [
          ...(await Promise.all(
            digitalData.map(async (company: any) => {
              if (!company.lenderName) {
                return {};
              }

              // const correspondingDebt = debts.find((entry: any) => {
              //   return entry.id == company.debtId;
              // });

              let type = "all";

              const obj: Company = {
                id: company.id,
                name: company.lenderName,
                reference: company.accountNumber,
                type: type as Company["type"],
                address: company.address || "",
                documents: company.documents || [],
                selectedDocuments: [],
                lastShared: company.lastShared || "",
                raw: company,
              };

              obj.agentSubmitted = (obj.raw?.submitDate && !obj.raw?.lastSharedDate) as boolean;

              const shareDate = company.lastSharedDate || company.submitDate;
              const shareDateMs = shareDate ? new Date(shareDate).getTime() : undefined;
              const canShareAgain = await shareBudgetsLogic(
                shareDateMs,
                undefined,
                false,
              );

              console.log("[DEBUG] companyShouldBeEnabled call | latestIeUpdateDateRef.current =", latestIeUpdateDateRef.current, "| company =", company.lenderName, "| result =", companyShouldBeEnabled(shareDate), "| shareTime =", new Date(shareDate).getTime(), "| docsCreationDate =", docsCreationDate, "| debtsCreationDate =", debtsCreationDate);

              // Auto-disable within 24h cooldown, but re-enable if new changes detected
              if (shareDate && isSameDay(shareDate) && !companyShouldBeEnabled(shareDate) && !canShareAgain) {
                if (!obj.agentSubmitted) {
                  console.debug(`[SharePage] Disabling ${obj.name} (id: ${obj.id}) due to 24h cooldown.`, { shareDate, canShareAgain });
                  obj.disableShare = true;
                  if (obj.raw) {
                    obj.raw.shareNow = "N";
                  }
                } else {
                  console.debug(`[SharePage] ${obj.name} (id: ${obj.id}) is agent-submitted; skipping cooldown disability.`);
                }
              } else if (canShareAgain && obj.disableShare) {
                console.debug(`[SharePage] Re-enabling ${obj.name} (id: ${obj.id}) because canShareAgain is true.`);
                obj.disableShare = false;
              } else if (canShareAgain && obj.raw?.shareNow === "N") {
                // re-enable share/declaration actions when budget/doc updates allow
                //obj.raw.shareNow = "Y"; - Causing side effects so commenting out
              }

              // PayPlan is shared once only: a shared PayPlan row is locked (delete only), whatever the 24h cooldown says
              if (
                (company.lenderName || "").trim().toLowerCase() === PAYPLAN_COMPANY_NAME.toLowerCase() &&
                (company.lastSharedDate || company.submitDate)
              ) {
                obj.disableShare = true;
                obj.isPayPlanShared = true;
                if (obj.raw) {
                  obj.raw.shareNow = "N";
                }
              }

              if (company.lenderName == "National Debtline") {
                obj.ndlData = {
                  debtAdviceContactPreferenceNdl: "Y",
                  emailMeNdl: true,
                  textMeNdl: hasMobileNumber,
                };
              }

              if (company.lenderName == "United Utilities") {
                const companyDeclarationData = declarationData.find((entry) => {
                  return entry.sharingId == company.id;
                });

                let declarationStatus: DeclarationStatusType =
                  companyDeclarationData?.declarationStatus == "Accept"
                    ? "accepted"
                    : companyDeclarationData?.declarationStatus == "Reject"
                      ? "rejected"
                      : "not-completed";

                obj.declarationStatus = declarationStatus;
                obj.declarationData = companyDeclarationData;
                obj.declarationStatus == "rejected"
                  ? (obj.raw.shareNow = "N")
                  : null;
              } else if (
                company.lenderName == "Yorkshire Water Community Trust"
              ) {
                const companyDeclarationData = declarationAllData.find(
                  (entry) => {
                    return entry.sharingId == company.id;
                  },
                );

                let declarationStatus: DeclarationStatusType =
                  companyDeclarationData?.declarationStatus == "Accept"
                    ? "accepted"
                    : companyDeclarationData?.declarationStatus == "Reject"
                      ? "rejected"
                      : "not-completed";

                obj.declarationData = companyDeclarationData;
                obj.declarationStatus = declarationStatus;
              } else if (company.lenderName == "Cadent Gas") {
                const data = cadentData.find((entry: ApplianceData) => {
                  return company.id == entry.sharingId;
                });

                let applianceCount = 0;

                if (data) {
                  const fieldsToCheck = [
                    "locationBoller",
                    "locationGasCooker",
                    "locationGasFire",
                    "locationGasHob",
                  ];

                  for (const field of fieldsToCheck) {
                    if (data[field]) {
                      applianceCount += 1;
                    }
                  }
                }

                obj.applianceCount = applianceCount;
              }

              try {
                const docsResponse = await getCompanyDocs(
                  company.id,
                  company.lenderName,
                  company.accountNumber,
                );
                const docsData = await docsResponse;

                // Normalize possible shapes: ServiceResponse[], ApiResponse, raw array
                const docsArray: SharedDocResponse[] = Array.isArray(
                  (docsData as any)?.data,
                )
                  ? ((docsData as any)?.data as SharedDocResponse[])
                  : Array.isArray((docsData as any)?.data?.data)
                    ? ((docsData as any)?.data?.data as SharedDocResponse[])
                    : Array.isArray(docsData)
                      ? (docsData as SharedDocResponse[])
                      : [];

                const documents: Document[] = docsArray.map(
                  (doc: SharedDocResponse) => ({
                    id: String(doc.id),
                    name: doc.documentName || doc.fileName || "Document",
                    size: doc.documentSize || "",
                    type: "wage-slip",
                    uploadedDate: doc.lastUpdateDate
                      ? formatDate(doc.lastUpdateDate)
                      : "",
                    raw: doc,
                  }),
                );

                obj.documents = documents;

                let selectedDocuments: string[] = [];

                for (const doc of documents) {
                  if (doc?.raw?.isShared == "Y") {
                    selectedDocuments.push(doc.id);
                  }
                }

                obj.selectedDocuments = selectedDocuments;
              } catch (e) {
                // eslint-disable-next-line no-console
                console.debug(
                  "An error occurred when fetching company docs",
                  e,
                );
              }

              // fetch payment offer data for company
              // try {
              //   const response = await httpGet<any>(
              //     debtEndpoints.paymentOffer.get(
              //       customerData.customerId,
              //       company.lenderName,
              //       company.accountNumber
              //     )
              //   );

              //   if (response.data.length > 0) {
              //     const { paymentOfferAmount, paymentPreferredFrequency } =
              //       response.data[0];

              //     obj.paymentOfferData = {
              //       paymentOfferAmount: paymentOfferAmount,
              //       paymentPreferredFrequency: ordinal(
              //         parseInt(paymentPreferredFrequency || "0")
              //       ),
              //     };
              //   }
              // } catch (e) {
              //   console.debug(
              //     "An error occurred when fetching payment offer data",
              //     e
              //   );
              // }

              return obj;
            }),
          )),

          ...(await Promise.all(
            freePostData.map(async (company: any) => {
              let type = "all";

              const shareDate = company.lastSharedDate || company.submitDate;
              const shareDateMs = shareDate ? new Date(shareDate).getTime() : undefined;
              const canShareAgain = await shareBudgetsLogic(
                undefined,
                shareDateMs,
                true,
              );

              const isFreepostCompanyInList = freepostCreditorNamesData.find((e: any) =>
                isPartialNameMatch(e.creditor, company.companyName)
              );

              let obj: Company = {
                id: company.id,
                name: company.companyName,
                reference: company.referenceNumber,
                type: type as Company["type"],
                address: company.address || "",
                documents: company.documents || [],
                selectedDocuments: [],
                lastShared: company.lastShared || "",
                raw: company,
                isFp: true,
                notSupported: !isFreepostCompanyInList,
              };

              // Auto-disable within 24h cooldown, but re-enable if new changes detected, force enable with companyShouldBeEnabled
              if (shareDate && isSameDay(shareDate) && !companyShouldBeEnabled(shareDate) && !canShareAgain) {
                console.debug(`[SharePage] Disabling freepost ${obj.name} (id: ${obj.id}) due to 24h cooldown.`, { shareDate, canShareAgain });
                obj.disableShare = true;
                if (obj.raw) {
                  obj.raw.shareNow = "N";
                }
              } else if (canShareAgain && obj.disableShare) {
                console.debug(`[SharePage] Re-enabling freepost ${obj.name} (id: ${obj.id}) because canShareAgain is true.`);
                obj.disableShare = false;
              } else if (canShareAgain && obj.raw?.shareNow === "N") {
                //obj.raw.shareNow = "Y"; - Causing side effects so commenting out
              }

              if (obj.notSupported) {
                 console.debug(`[SharePage] Disabling ${obj.name} (id: ${obj.id}) because creditor is not in the supported freepost list.`);
                 obj.disableShare = true;
              }

              return obj;
            }),
          )),
        ];

        return data;
      } catch (error) {
        console.error("Failed to fetch companies", error);
        return [];
      }
    };

    fetchCompanies().then((companiesArray) => {
      setCompanies(sortBySourceAndCreationDate(companiesArray));
      setHasCompaniesLoaded(true);
    });
  }, [declarationData, debts, isSummaryLoaded, ieMstrLenderDataLoaded, loadedFreepostCreditorNamesData]);

  const [expandedCompanies] = useState<string[]>([]);
  const [declarationOpen, setDeclarationOpen] = useState(false);
  const [hasLoadedDeclarationData, setHasLoadedDeclarationData] =
    useState(false);

  const [householdDeclaration, setHouseholdDeclaration] =
    useState<HouseholdDeclaration>({
      status: "no",
      circumstances: "no",
      previousCredit: "no",
      financialPositionChange: "no",
    });
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [addCompanyOpen, setAddCompanyOpen] = useState(false);
  const [shareHistoryOpen, setShareHistoryOpen] = useState(false);

  // ==================== Computed Values ====================

  // Open dialog if URL contains additionSupport=true
  useEffect(() => {
    if (searchParams.get("additionSupport") === "true") {
      setDeclarationOpen(true);
    }
  }, [searchParams]);

  const hasFetchedPreferences = useRef(false);

  // GET customer preferences data: loads NDL's saved call time and email choice into page state
  useEffect(() => {
    if (!companies || companies.length == 0) {
      return;
    }

    if (hasFetchedPreferences.current) return;
    hasFetchedPreferences.current = true;

    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    async function getCustomerPreferencesData() {
      const { data } = await httpGet<CustomerPreferencesEntry[]>(
        customerEndpoints.getCustomerPreferences(customerData.customerId),
      );

      if (data.length > 0) {
        for (const entry of data) {
          const company = companies.find((c) => c.raw?.id == entry.sharingId);

          if (!company) {
            continue;
          }

          const obj = entry as CustomerPreferencesEntry;

          setCustomerPrefrences(company.raw?.id as number, obj);
          setIsPreferencesLoaded(company.raw?.id as number, true);

          setPreferredTimeForNdl(
            company.raw?.id as number,
            obj.preferredTimeForNdl,
          );
          // setDebtAdviceContactPreferenceNdl(
          //   company.raw?.id as number,
          //   obj.debtAdviceContactPreferenceNdl
          // );
          setVoicemailConsentNdl(
            company.raw?.id as number,
            obj.voicemailConsentNdl == "Y",
          );
          setEmailMeNdl(company.raw?.id as number, obj.emailMeNdl == "Y");
          setTextMeNdl(company.raw?.id as number, obj.textMeNdl == "Y");

          // PayPlan and NDL: the backend's saved preferences win, unless the customer saved a change on this device after them
          if (CONTACT_COMPANIES.includes(company.name) && !choiceIsNewer(readContactChoices()[company.name], obj.lastUpdateDate)) {
            const saved = contactDetailsFromRow(obj, profileContact);
            if (saved) setContactDetails((prev) => ({ ...prev, [company.name]: saved }));
          }
        }
      }

      return;
    }

    getCustomerPreferencesData();
  }, [companies]);

  // GET lender data
  useEffect(() => {
    async function getCustomerShareData() {
      const { data } = await LenderService.fetchLenders({
        skipErrorCapture: true,
      });

      if (data) {
        setIeMstrLenderData(data);
      }
      setIeMstrLenderDataLoaded(true);
    }

    getCustomerShareData();
  }, []);

  // GET Digital Creditor List Names
  useEffect(() => {
    async function getCreditorNames(): Promise<void> {
      try {
        const response = await httpGet<ApiResponse>(
          customerEndpoints.listDigitalLenders,
        );

        if (response.data && Array.isArray(response.data)) {
          setDigitalLenders((response.data as DigitalLender[]).filter((d) => !!d.creditor));
        }
      } catch (error) {
        femaCapture("fetching_creditor_list_data_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
      }
    }

    getCreditorNames();
  }, []);

  const groupedCompanies = useMemo(() => {
    const groups: Record<string, Company[]> = {
      debt: [],
      "additional-support": [],
      added: [],
      "referred-linked": [],
      referred: [],
      all: [],
    };

    companies.forEach((company) => {
      const group = groups[company.type];
      if (company.raw?.isSharing != "N" && company.raw?.isDeleted != "Y") {
        if (group) {
          group.push(company);
        }
      }
    });

    groups["debt"] = [
      ...(groups["referred-linked"] ?? []),
      ...(groups["debt"] ?? []),
    ];

    return groups;
  }, [companies]);

  // Calculate total unique companies being shared
  const totalCompaniesCount = useMemo(() => {
    const filteredCompanies = companies.filter(
      (c) =>
        c.raw?.isSharing !== "N" &&
        c.raw?.isDeleted !== "Y" &&
        c.raw?.shareNow == "Y",
    );
    const uniqueNames = new Set(filteredCompanies.map((c) => c.name));
    return uniqueNames.size;
  }, [companies]);

  const allCOunt = useMemo(
    () =>
      companies.filter(
        (c) =>
          c.type == "all" && c.raw?.isDeleted != "Y" && c.raw?.isSharing == "Y",
      ).length,
    [companies],
  );

  const hasNoCompanies = useMemo(() => {
    return [allCOunt].every((count) => count == 0 || !count);
  }, [allCOunt, companies]);

  useEffect(() => {
    setCompanies((prev) => sortBySourceAndCreationDate(prev));
  }, [companies.length]);

  // ==================== Event Handlers ====================

  // Scroll to section
  const scrollToSection = useCallback((section: TabType) => {
    let ref: React.RefObject<HTMLDivElement> | null = null;

    switch (section) {
      case "all":
        ref = allSectionRef;
        break;
      case "referred":
        ref = referredSectionRef;
        break;
      case "debts":
        ref = debtsSectionRef;
        break;
      case "additional-support":
        ref = additionalSupportSectionRef;
        break;
      case "added":
        ref = addedSectionRef;
        break;
    }

    if (ref?.current) {
      ref.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

      const elm = document.getElementById(`${section}-section`);
      if (elm) elm.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  const handleTabChange = useCallback(
    (tab: TabType) => {
      DashboardService.logButtonAction({
        actionType: ActionType.TAB_CLICK,
        actionName: `Share Tab: ${tab}`,
        pageName: "Share",
        subSection: "Tabs",
      }).catch(() => {});
      setActiveTab(tab);
      scrollToSection(tab);
    },
    [scrollToSection],
  );

  const toggleEditCompanyDialog = (company: Company) => {
    setSelectedEditCompany(company);
    setOpenEditDialog(true);
  };

  const toggleDeleteCompanyDialog = (company: Company) => {
    setSelectedEditCompany(company);
    setOpenDeleteDialog(true);
  };

  // Load mobile and email from the customer record, the same source as the profile page.
  useEffect(() => {
    const userId = JSON.parse(localStorage.getItem("customerData") || "{}")?.userId;
    if (!userId) return;
    (async () => {
      try {
        const resp = await OnboardingService.getCustomerByUserId(userId, { skipErrorCapture: true });
        const record = Array.isArray(resp?.data) ? resp.data[0] : resp?.data;
        if (!record || contactSavedRef.current) return;
        const next = { mobile: record.mobileNumber ?? "", email: record.emailAddress ?? "" };
        setProfileContact(next);
        const defaults = defaultContacts(next);
        // Entries seeded from the cached number take the fresh profile number/email; if the fresh one is unusable, fall back to the defaults.
        setContactDetails((prev) =>
          Object.fromEntries(
            CONTACT_COMPANIES.map((name) => {
              const d = prev[name];
              if (!d) return [name, defaults[name]];
              const usable = d.method === "phone" ? isValidPhoneNumber(next.mobile) : next.email.trim() !== "";
              return [name, usable ? { ...d, mobile: next.mobile, email: next.email } : defaults[name]];
            }),
          ),
        );
      } catch {
        // keep the cached values when the customer record can't be loaded
      }
    })();
  }, []);

  // Save a changed mobile number and/or email to the customer's profile (same step-5 save as the profile page).
  // Resolves true when saved (or nothing changed) and false on failure, so the cards can wait for the result.
  const saveContactToProfile = async (changes: { mobile?: string; email?: string }): Promise<boolean> => {
    const newMobile = changes.mobile !== undefined ? cleanPhoneNumber(changes.mobile) : undefined;
    const newEmail = changes.email?.trim();
    const mobileChanged = newMobile !== undefined && newMobile !== cleanPhoneNumber(profileContact.mobile);
    const emailChanged = newEmail !== undefined && newEmail !== profileContact.email.trim();
    if (!mobileChanged && !emailChanged) return true;
    try {
      const stored = JSON.parse(localStorage.getItem("customerData") || "{}");
      await OnboardingService.updateCustomer(
        {
          "onboarding-step-5": {
            ...(mobileChanged && { phoneNumber: newMobile }),
            ...(emailChanged && { emailId: newEmail }),
          },
        },
        stored.userId,
        stored.id ?? stored.customerId,
        stored.customerId ?? stored.id,
        stored.emailAddress || "",
        { skipErrorCapture: false },
      );
      contactSavedRef.current = true;
      if (mobileChanged) setHasMobileNumber(true);
      setProfileContact((prev) => ({
        mobile: mobileChanged ? (newMobile as string) : prev.mobile,
        email: emailChanged ? (newEmail as string) : prev.email,
      }));
      // Keep every card's saved details in step, so editing on either card updates both.
      setContactDetails((prev) =>
        Object.fromEntries(
          Object.entries(prev).map(([name, d]) => [
            name,
            d
              ? {
                  ...d,
                  mobile: mobileChanged ? (newMobile as string) : d.mobile,
                  email: emailChanged ? (newEmail as string) : d.email,
                }
              : d,
          ]),
        ),
      );
      return true;
    } catch {
      showAlert({
        severity: "error",
        message: "Failed to save your contact details to your profile",
        autoHide: true,
        duration: 2000,
      });
      return false;
    }
  };

  // Keep a company's contact details; a changed mobile or email is also saved to the customer's profile.
  const handleContactSave = async (companyName: string, details: ContactDetails): Promise<boolean> => {
    // Save first; show the new details only once the profile save has succeeded.
    const changes = details.method === "phone" ? { mobile: details.mobile } : { email: details.email };
    if (!(await saveContactToProfile(changes))) return false;
    setContactDetails((prev) => ({ ...prev, [companyName]: details }));
    setContactErrors((prev) => ({ ...prev, [companyName]: false }));
    saveContactChoice(companyName, details); // remember the choice
    return true;
  };

  const toggleInfoDialog = (company: Company) => {
    setSelectedEditCompany(company);
    setOpenInfoDialog(true);
  };

  const handleDocumentToggle = useCallback(
    (companyId: string, documentId: string, findId?: boolean) => {
      if (findId) {
        documentId =
          companies
            .find((c) => c.id == companyId)
            ?.documents.find((d) => d.raw?.documentId == parseInt(documentId))
            ?.id || "0";
      }

      console.log(companyId, documentId);

      setCompanies((prev) =>
        prev.map((company) => {
          if (company.id === companyId) {
            const isSelected = company.selectedDocuments.includes(documentId);

            const document = company.documents.find(
              (entry) => entry.id == documentId,
            );

            const isShared = isSelected ? "N" : "Y";

            const updateSharingDoc = async (payload: SharedDocResponse) => {
              await httpPut<CustomerSharingResponse>(
                documentsPageEndpoints.updateSharedDocuments(payload.id),
                payload,
              );
            };

            updateSharingDoc({
              ...(document?.raw as SharedDocResponse),
              isShared,
            });

            // set declaraton data selected documents for UU document checking functionality
            if (company.id == declarationCompany.id) {
              setDeclarationCompany({
                ...declarationCompany,
                selectedDocuments: isSelected
                  ? company.selectedDocuments.filter((id) => id !== documentId)
                  : [...company.selectedDocuments, documentId],
              });
            }

            return {
              ...company,
              selectedDocuments: isSelected
                ? company.selectedDocuments.filter((id) => id !== documentId)
                : [...company.selectedDocuments, documentId],
            };
          }
          return company;
        }),
      );
    },
    [companies, declarationCompany],
  );

  const handleFileUpload = useCallback((files: FileList | null) => {
    if (!files) return;

    const newFiles: UploadedFile[] = Array.from(files).map((file) => ({
      id: `file-${Date.now()}-${Math.random()}`,
      name: file.name,
      size: file.size,
      uploadDate: new Date(),
      file,
    }));

    setUploadedFiles((prev) => [...prev, ...newFiles]);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      handleFileUpload(e.dataTransfer.files);
    },
    [handleFileUpload],
  );

  const handleRemoveFile = useCallback((fileId: string) => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
  }, []);

  const handleDeclarationClose = useCallback(() => {
    setDeclarationOpen(false);
    const next = new URLSearchParams(searchParams);
    next.delete("additionSupport");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleDeclarationSubmit = useCallback(() => {
    setDeclarationOpen(false);
    const next = new URLSearchParams(searchParams);
    next.delete("additionSupport");
    setSearchParams(next, { replace: true });
  }, [
    householdDeclaration,
    uploadedFiles,
    companies,
    searchParams,
    setSearchParams,
  ]);

  const formatFileSize = useCallback((bytes: number): string => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  }, []);

  // ==================== Render Functions ====================

  const renderHeader = () => (
    <Box data-testid="pages-private-dashboard-render-header-container-action-1" sx={{ mb: isMobile ? 3 : 4 }}>
      <Typography data-testid="pages-private-dashboard-render-header-text-share-your-budget"
        variant={isMobile ? "h5" : "h4"}
        sx={{
          fontWeight: 600,
          color: theme.palette.primary.main,
          mb: 2,
        }}
      >
        Share Your Budget
      </Typography>
      <Typography data-testid="pages-private-dashboard-render-header-text-action"
        variant="body1"
        sx={{
          color: theme.palette.text.secondary,
          mb: 3,
          lineHeight: 1.6,
        }}
      >
        Manage your budget sharing with financial institutions and support
        organisations. Review, share, and track your submissions all in one
        place.
      </Typography>
      <Box data-testid="pages-private-dashboard-render-header-container-action-2"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          cursor: "pointer",
          color: theme.palette.primary.main,
          "&:hover": {
            textDecoration: "underline",
          },
          width: "fit-content",
        }}
        // onClick={() => setDeclarationOpen(true)}
      >
        <Typography data-testid="pages-private-dashboard-render-header-text-check-your-support-options"
          variant="body1"
          sx={{
            fontWeight: 500,
            color: "#155F54",
            textDecoration: "underline",
          }}
          onClick={() => {
            DashboardService.logButtonAction({
              actionType: ActionType.BUTTON_CLICK,
              actionName: "Check_Your_Support_Options",
              pageName: "Share",
              subSection: "Header",
            }).catch(() => {});
            setIsAdditionalSupportOpen(true);
          }}
        >
          Check Your Support Options
        </Typography>
        <ArrowForwardIcon sx={{ fontSize: 22 }} />
      </Box>
    </Box>
  );

  const renderTabs = () => (
    <>
      <Box data-testid="pages-private-dashboard-render-tabs-container-action-1"
        sx={{
          display: "flex",
          gap: isMobile ? 1 : 2,
          mb: 3,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <Box data-testid="pages-private-dashboard-render-tabs-container-action-2" sx={{ display: "flex", gap: 2, width: "100%" }}>
          <Box data-testid="pages-private-dashboard-render-tabs-container-action-3"
            onClick={() => {
              DashboardService.logButtonAction({
                actionType: ActionType.BUTTON_CLICK,
                actionName: "Share_History",
                pageName: "Share",
                subSection: "Tabs",
              }).catch(() => {});
              setShareHistoryOpen(true);
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              backgroundColor: "white",
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1.5,
              p: 1.5,
              cursor: "pointer",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            <HistoryIcon />
            <Typography data-testid="pages-private-dashboard-render-tabs-text-share-history"
              variant="body1"
              sx={{ fontWeight: 500, color: "#495057" }}
            >
              Share History
            </Typography>
          </Box>

          <Box data-testid="pages-private-dashboard-render-tabs-container-action-4"
            onClick={() => {
              DashboardService.logButtonAction({
                actionType: ActionType.BUTTON_CLICK,
                actionName: "Add_Company",
                pageName: "Share",
                subSection: "Tabs",
              }).catch(() => {});
              setAddCompanyOpen(true);
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 1,
              flex: 1, // take remaining width
              backgroundColor: "#DFE7E2",
              border: "2px dashed #7BA39B", // dash color
              borderRadius: 1.5,
              cursor: "pointer",
              color: theme.palette.primary.main,
              fontWeight: 500,
              "&:hover": {
                backgroundColor: "#cfd8cc",
              },
            }}
          >
            <Typography data-testid="pages-private-dashboard-render-tabs-text-+-add-company"
              variant="body1"
              sx={{
                fontWeight: 600,
                textAlign: "center",
                width: "100%",
              }}
            >
              + Add Company
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );

  // "Add to share" tick: updates the page, then saves shareNow to the backend straight away
  async function toggleCompanyShare(company: Company) {
    const newCompany = {
      ...company,
      raw: {
        ...company.raw,
        shareNow: company.raw?.shareNow == "Y" ? "N" : "Y",
      },
    };

    // if (!company.isFp) {
    //   newCompany.raw.isSharing = company.raw?.shareNow == "Y" ? "N" : "Y";
    // }

    setCompanies(
      companies.map((entry: any) => {
        if (entry.id == company.id) {
          entry = newCompany;
        }

        return entry;
      }),
    );

    let newResp;

    if (!company.isFp) {
      const sanitizedRaw = { ...newCompany.raw };
      delete (sanitizedRaw as any).editable;
      newResp = await httpPut<CustomerSharingResponse>(
        sharePageEndpoints.createCustomerSharing(),
        sanitizedRaw,
      );
    } else {
      const sanitizedRaw = { ...newCompany.raw };
      delete (sanitizedRaw as any).editable;
      newResp = await httpPut<CustomerSharingResponse>(
        sharePageEndpoints.createFreepostSharing(),
        sanitizedRaw,
      );
    }

    if (company.raw?.shareNow == "N") {
      setCompanyShareLastUpdateDate(
        new Date(
          newResp.data.lastUpdateDate || companyShareLastUpdateDate,
        ).getTime(),
      );
    }
  }

  function companyShouldBeEnabled(companyLastSharedDate?: string): boolean {
    if (!companyLastSharedDate) {
      return true;
    }
    // companyLastSharedDate comes from the backend as UTC+1, so subtract 1 hour to normalise to UTC 0
    const shareTime = new Date(companyLastSharedDate).getTime(); // - 60 * 60 * 1000;

    // Timezone offset in milliseconds
    //const offsetMs = new Date().getTimezoneOffset() * 60 * 1000;

    // Normalize creation/update dates (which are in UTC 0 but parsed locally) 
    // to match the absolute UTC timestamp reference frame.
    const normalizedDocs = docsCreationDate;
    const normalizedDebts = debtsCreationDate;
    // const normalizedCompanyShare = companyShareLastUpdateDate - offsetMs;
    // const normalizedFreePost = freePostLastUpdateDate - offsetMs;
    // const normalizedLatestIeUpdate = latestIeUpdateDate - offsetMs;

    console.log("shareTime", shareTime, latestIeUpdateDateRef.current);

    return (
      shareTime < normalizedDocs ||
      shareTime < normalizedDebts ||
      shareTime < latestIeUpdateDateRef.current
      //shareTime < normalizedCompanyShare ||
      //shareTime < normalizedFreePost
    );
  }

  function isShareCooldownOver(
    companyLastSharedDateL?: number,
    freePostLastSharedDateL?: number,
    isFp?: boolean,
  ) {
    const now = Date.now();

    // Use the local arguments if provided, otherwise fallback to outer scoped variables
    const companyDate = isFp !== undefined ? companyLastSharedDateL : (companyLastSharedDateL ?? companyLastSharedDate);
    const freePostDate = isFp !== undefined ? freePostLastSharedDateL : (freePostLastSharedDateL ?? freePostLastSharedDate);

    const companyCooldownEnd = companyDate ? new Date(companyDate).setHours(23, 59, 59, 999) : 0;
    const companyOk = isFp === true ? true : (!companyDate || now > companyCooldownEnd);

    // eslint-disable-next-line no-console
    console.debug(
      companyOk
        ? `Company share cooldown OK${companyDate ? ` (last shared: ${new Date(companyDate).toISOString()}, cooldown ended: ${new Date(companyCooldownEnd).toISOString()})` : " (never shared)"}`
        : `Company share cooldown NOT over (last shared: ${new Date(companyDate).toISOString()}, cooldown ends: ${new Date(companyCooldownEnd).toISOString()})`,
    );

    const freePostCooldownEnd = freePostDate ? new Date(freePostDate).setHours(23, 59, 59, 999) : 0;
    const freePostOk = isFp === false ? true : (!freePostDate || now > freePostCooldownEnd);

    // eslint-disable-next-line no-console
    console.debug(
      freePostOk
        ? `Free post share cooldown OK${freePostDate ? ` (last shared: ${new Date(freePostDate).toISOString()}, cooldown ended: ${new Date(freePostCooldownEnd).toISOString()})` : " (never shared)"}`
        : `Free post share cooldown NOT over (last shared: ${new Date(freePostDate).toISOString()}, cooldown ends: ${new Date(freePostCooldownEnd).toISOString()})`,
    );

    const result = companyOk && freePostOk;

    // eslint-disable-next-line no-console
    console.debug(
      result
        ? "Cooldown IS over: all required conditions met"
        : `Cooldown NOT over: one or more conditions failed. Details: ` +
          [
            !companyOk && `Company share cooldown NOT over (last shared: ${new Date(companyDate!).toISOString()}, cooldown ends: ${new Date(companyCooldownEnd).toISOString()})`,
            !freePostOk && `Free post share cooldown NOT over (last shared: ${new Date(freePostDate!).toISOString()}, cooldown ends: ${new Date(freePostCooldownEnd).toISOString()})`
          ].filter(Boolean).join(", "),
    );

    // eslint-disable-next-line no-console
    console.debug("cooldown", companyDate, freePostDate, Date.now());

    return result;
  }

  async function shareBudgetsLogic(
    companyLastSharedDateL?: number,
    freePostLastSharedDateL?: number,
    isFp?: boolean,
  ) {
    console.log("companyLastSharedDateL ", companyLastSharedDateL)

    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    customerData.submitDate =
      (localStorage.getItem("submitDate") as string) || customerData.submitDate;

    if (customerData.submitDate == null || !customerData.submitDate) {
      console.debug("[SharePage] shareBudgetsLogic: true - No submit date found.");
      return true;
    }

    const submitDate = new Date(customerData.submitDate);
    const milliseconds = submitDate.getTime();

    let submitDateAddedMilliSecs = new Date(customerData.submitDate);
    submitDateAddedMilliSecs.setTime(submitDateAddedMilliSecs.getTime() + 5000);
    const millisecondsAfterAdded = submitDateAddedMilliSecs.getTime();

    // Create a new Date object for 11:59:59.999 PM of the same day
    if (isShareCooldownOver(companyLastSharedDateL, freePostLastSharedDateL, isFp)) {
      console.debug("[SharePage] shareBudgetsLogic: true - 24h cooldown is over.");
      return true;
    }

    const prevBalanceAmount = latestSnapshot?.balanceAmount || 0;

    if (summary.balanceAmount != prevBalanceAmount) {
      console.debug("[SharePage] shareBudgetsLogic: true - Balance amount changed.", {
        prevBalanceAmount,
        currentBalance: summary.balanceAmount
      });
      return true;
    }

    const isCompanyUpdateNewer = companyShareLastUpdateDate > millisecondsAfterAdded;
    const isDocsUpdateNewer = docsCreationDate > milliseconds;
    const isDebtsUpdateNewer = debtsCreationDate > milliseconds;
    const isFreePostUpdateNewer = freePostLastUpdateDate > milliseconds;

    const formatDate = (ms: number) => ms ? new Date(ms).toISOString() : "never (0)";

    if (
      isCompanyUpdateNewer ||
      isDocsUpdateNewer ||
      isDebtsUpdateNewer ||
      isFreePostUpdateNewer
    ) {
      const reasons = [];
      if (isCompanyUpdateNewer) {
        reasons.push(`companyShareLastUpdateDate (${formatDate(companyShareLastUpdateDate)} > submitDate + 5s: ${formatDate(millisecondsAfterAdded)})`);
      }
      if (isDocsUpdateNewer) {
        reasons.push(`docsCreationDate (${formatDate(docsCreationDate)} > submitDate: ${formatDate(milliseconds)})`);
      }
      if (isDebtsUpdateNewer) {
        reasons.push(`debtsCreationDate (${formatDate(debtsCreationDate)} > submitDate: ${formatDate(milliseconds)})`);
      }
      if (isFreePostUpdateNewer) {
        reasons.push(`freePostLastUpdateDate (${formatDate(freePostLastUpdateDate)} > submitDate: ${formatDate(milliseconds)})`);
      }

      console.debug(`[SharePage] shareBudgetsLogic: true - Newer data detected since last share (overriding cooldown). Reasons: ${reasons.join(", ")}`, {
        companyShareUpdate: isCompanyUpdateNewer,
        docsUpdate: isDocsUpdateNewer,
        debtsUpdate: isDebtsUpdateNewer,
        freepostUpdate: isFreePostUpdateNewer,
        companyShareLastUpdateDate,
        docsCreationDate,
        debtsCreationDate,
        freePostLastUpdateDate,
        milliseconds,
        millisecondsAfterAdded
      });
      return true;
    }

    const notOverReasons = [];
    notOverReasons.push(`companyShareLastUpdateDate (${formatDate(companyShareLastUpdateDate)} <= submitDate + 5s: ${formatDate(millisecondsAfterAdded)})`);
    notOverReasons.push(`docsCreationDate (${formatDate(docsCreationDate)} <= submitDate: ${formatDate(milliseconds)})`);
    notOverReasons.push(`debtsCreationDate (${formatDate(debtsCreationDate)} <= submitDate: ${formatDate(milliseconds)})`);
    notOverReasons.push(`freePostLastUpdateDate (${formatDate(freePostLastUpdateDate)} <= submitDate: ${formatDate(milliseconds)})`);

    console.debug(`[SharePage] shareBudgetsLogic: false - Cooldown active and no changes detected. Details: ${notOverReasons.join(", ")}`, {
      companyShareLastUpdateDate,
      docsCreationDate,
      debtsCreationDate,
      freePostLastUpdateDate,
      milliseconds,
      millisecondsAfterAdded
    });
    return false;
  }

  async function getLatestIeUpdateDate() {
    try {
      const storedCustomerRaw = localStorage.getItem("customerData");
      const storedCustomer = storedCustomerRaw ? JSON.parse(storedCustomerRaw) : null;
      const customerId = storedCustomer?.customerId || storedCustomer?.id;

      if (!customerId) {
        console.warn("[SharePage] getLatestIeUpdateDate: No customerId found.");
        return;
      }

      const response = await httpGet<ApiResponse>(
        latestUpdateEndpoints.fetchLatestUpdate(Number(customerId)),
      );

      console.log("getLatestIeUpdateDate", response.data);

      const dateStr = response.data[0].lastUpdateDate;

      if (dateStr) {
        const time = new Date(dateStr).getTime();
        console.log("[DEBUG] getLatestIeUpdateDate raw dateStr =", dateStr, "| parsed time =", time);
        if (!isNaN(time)) {
          latestIeUpdateDateRef.current = time;
          setLatestIeUpdateDate(time);
          console.log("[DEBUG] latestIeUpdateDateRef.current SET to", time);
        }
      }
    } catch (error) {
      femaCapture("fetching_latest-snapshot_data_error", error, {
        category: "api",
        severity: "error",
        skipSanitization: false,
        user: {},
      });
    }
  }

  const hasEnabledDeclaration = useRef(false);

  //Re-enable sharing/declaration after budget/doc/company updates pass cooldown
  useEffect(() => {
    if (
      hasEnabledDeclaration.current ||
      !hasLoadedDeclarationData ||
      !debts ||
      !isSummaryLoaded ||
      !ieMstrLenderDataLoaded ||
      !companies ||
      companies.length == 0
    ) {
      return;
    }

    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    hasEnabledDeclaration.current = true;

    (async () => {
      console.log("[DEBUG] getLatestIeUpdateDate() called at", Date.now());
      await getLatestIeUpdateDate();
      console.log("[DEBUG] getLatestIeUpdateDate() resolved at", Date.now(), "| latestIeUpdateDateRef.current =", latestIeUpdateDateRef.current);
      setCompanies((prev) => {
        let changed = false;
        const next = prev.map((company) => {
          const shouldEnable =
            company.disableShare || company.raw?.shareNow === "N";

          // Always reset declaration when we can share again
          const shouldResetDeclaration =
            (company.name === "United Utilities" ||
              company.name === "Yorkshire Water Community Trust") &&
            company.declarationStatus === "accepted";

          const epochMilliseconds = company.declarationData
            ? (company.declarationData.customerAgreedDate ||
                company.declarationData.lastUpdateDate / 1000) * 1000
            : 0;

          const submitDateAddedMilliSecs = new Date(customerData.submitDate);
          submitDateAddedMilliSecs.setTime(
            submitDateAddedMilliSecs.getTime() + 5000,
          );

          const millisecondsAfterAdded = submitDateAddedMilliSecs.getTime();

          const prevBalanceAmount = latestSnapshot?.balanceAmount || 0;

          const canShareAgain =
            docsCreationDate > epochMilliseconds ||
            debtsCreationDate > epochMilliseconds //||
            //summary.balanceAmount != prevBalanceAmount;

          if (!canShareAgain) {
            return company;
          }

          if (!shouldResetDeclaration) {
            //const needsUpdate = shouldEnable && shouldResetDeclaration;

            return company;
          }

          const updated: Company = {
            ...company,
            ...(shouldEnable ? { disableShare: false } : {}),
            ...(company.raw
              ? {
                  raw: {
                    ...company.raw,
                    shareNow: "Y",
                  },
                }
              : {}),
            ...(shouldResetDeclaration
              ? {
                  declarationStatus: "not-completed" as DeclarationStatusType,
                }
              : {}),
          };

          changed = true;
          return updated;
        });

        return changed ? next : prev;
      });
    })();
  }, [
    hasEnabledDeclaration,
    hasLoadedDeclarationData,
    debts,
    isSummaryLoaded,
    ieMstrLenderDataLoaded,
    companies,
  ]);

  function getUniqueCompanieNames() {
    const seenNames: Set<string> = new Set();

    for (const company of companies) {
      const name = company.raw?.creditor || company.name;
      if (company.raw?.shareNow === "Y" && !seenNames.has(name)) {
        seenNames.add(name);
      }
    }

    // Convert Set to array before joining
    return Array.from(seenNames).join(", ");
  }

  function renderCompanyReference(reference: string) {
    return reference == emailAddress ? "" : reference;
  }

  // "Share securely" handler: validates, sends everything, saves NDL preferences, then opens the success page
  async function shareData() {
    // First check of all: PayPlan or NDL on a phone call with no call time picked stops the share before anything is sent.
    const needsCallTime = companies.find(
      (company) =>
        CONTACT_COMPANIES.includes(company.name) &&
        company.raw?.shareNow == "Y" &&
        company.raw?.isSharing != "N" &&
        company.raw?.isDeleted != "Y" &&
        callTimeMissing(contactDetails[company.name], company.name === "PayPlan"),
    );
    if (needsCallTime) {
      showAlert({
        severity: "warning",
        message: `Please choose a time for ${needsCallTime.name} to call you.`,
        autoHide: true,
        duration: 2000,
      });
      setContactErrors((prev) => ({ ...prev, [needsCallTime.name]: true }));
      scrollToCompanyCard(needsCallTime.id);
      return;
    }

    setIsSharing(true);
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    const ineligible = companies
      .filter(
        (company) =>
          company.raw?.shareNow === "Y" &&
          isCoreAspCompany(company.name) &&
          company.name.trim() !== PAYPLAN_COMPANY_NAME && // PayPlan is exempt from the ASP eligibility check
          ((company.raw?.source === "unlinked-debt" ||
            company.raw?.source === "unlink_customer_profile" ||
            (company.raw?.source === "customer_profile" &&
              (customerData?.field5TxtFld || customerData?.field5Txtfld)?.trim().toLowerCase() !==
                company.name.trim().toLowerCase()) ||
            company.raw?.source === "additional-support" ||
            (company.raw?.source === "debts" && !company.raw?.debtId)) &&
            eligibleAspConditions !== null &&
            !isEligibleAspCondition(eligibleAspConditions, company.name)),
      )
      .map((c) => c.name);

    if (ineligible.length > 0) {
      console.debug("[SharePage] Identified ineligible companies for sharing:", ineligible);
      setIneligibleCompanies(ineligible);
      setUpdatedRecommendationsModalSource("share_securely");

      // Locally uncheck these companies so they are "removed" from the current share selection
      setCompanies((prev) =>
        prev.map((c) => {
          if (ineligible.includes(c.name)) {
            const updatedRaw = {
              ...c.raw,
              shareNow: "N",
            };

            const updateIneligibleStatus = async () => {
              try {
                const sanitizedRaw = { ...updatedRaw };
                delete (sanitizedRaw as any).editable;

                if (c.isFp) {
                  await httpPut(sharePageEndpoints.createFreepostSharing(), sanitizedRaw);
                } else {
                  await httpPut(sharePageEndpoints.createCustomerSharing(), sanitizedRaw);
                }
              } catch (e) {
                console.error("Failed to update ineligible company status", e);
              }
            };
            updateIneligibleStatus();

            return {
              ...c,
              raw: updatedRaw,
            };
          }
          return c;
        }),
      );

      setShowUpdatedRecommendationsModal(true);
      setIsSharing(false);
      return;
    }

    // Company validation: stop the share if a company still needs something (declaration, NDL answers, PayPlan details)
    for (const company of companies) {
      if (company.raw?.isSharing == "N" || company.raw?.isDeleted == "Y ") {
        continue;
      }

      const declarationCompanies = [
        "United Utilities",
        "Yorkshire Water Community Trust",
      ];

      if (
        declarationCompanies.includes(company.name) &&
        company.declarationStatus != "accepted" &&
        company.raw?.shareNow == "Y"
      ) {
        showAlert({
          severity: "warning",
          message: `Please read and accept the declaration from ${company.name} to proceed.`,
          autoHide: true,
          duration: 2000,
        });

        setIsSharing(false);
        return;
      }

      // PayPlan or NDL selected but its contact details are not saved (or still being edited): hold the share and jump to the card.
      if (CONTACT_COMPANIES.includes(company.name) && company.raw?.shareNow == "Y" && (!contactDetails[company.name] || contactEditing[company.name])) {
        showAlert({
          severity: "warning",
          message: `Please save your contact details for ${company.name} to continue.`,
          autoHide: true,
          duration: 2000,
        });
        setContactErrors((prev) => ({ ...prev, [company.name]: true }));
        scrollToCompanyCard(company.id);
        setIsSharing(false);
        return;
      }

    }

    // Notify the way the customer chose on the card (phone = SMS, email = email); with no saved choice, SMS when there is a mobile number
    const getNotificationSource = (details?: ContactDetails, mobileNumber?: string) =>
      details ? (details.method === "phone" ? "sendSMS" : "sendEmail") : mobileNumber ? "sendSMS" : "sendEmail";

    for (const company of companies) {
      if (company.raw?.shareNow != "Y") {
        continue;
      }

      if (company.name === "National Debtline") {
        const chosen = contactDetails[company.name];
        const mobileNumber = chosen?.mobile || customerData.mobileNumber;
        const payload: NotifyCustomerDebtAdvice = {
          companyName: customerData.field5TxtFld,
          userId: customerData.userId,
          isUnitedUtilitiesCompany: "N",
          mobileNumber,
          notificationSource: getNotificationSource(chosen, mobileNumber),
          debtAdviceContactPreNdl: chosen
            ? preferenceFields(chosen, false).debtAdviceContactPreferenceNdl
            : (company.ndlData?.debtAdviceContactPreferenceNdl ?? "Y"),
        };

        await httpPost<NotifyCustomerDebtAdviceResponse>(
          customerEndpoints.notifyCustomerDebtAdvice(),
          payload,
        );
        continue;
      }

      if (["PayPlan", "Make Life Better"].includes(company.name)) {
        const payload: NotifyCustomerDebtAdvice = {
          companyName: company.name,
          userId: customerData.userId,
          mobileNumber: customerData.mobileNumber,
          notificationSource: "sendEmail",
          ...(company.name === "PayPlan" && payPlanNotifyFields(contactDetails[company.name])), // PayPlan only: channel and call time
        };

        await httpPost<NotifyCustomerDebtAdviceResponse>(
          customerEndpoints.notifyCustomerDebtAdvice(),
          payload,
        );
      }

      if (company.name == "Cadent Gas") {
        const payload: SendEmailOnShareEntry = {
          companyName: company.name,
        };

        await httpPost<SendEmailOnShareEntry>(
          customerEndpoints.sendEmailOnShare(),
          payload,
        );
      }
    }

    const resp = await shareBudgetsLogic();

    const companyToShareCount = companies.filter(
      (company) =>
        company.raw?.shareNow == "Y" &&
        company.raw?.isDeleted !== "Y" &&
        company.raw?.isSharing !== "N",
    ).length;

    const hasDisabledShareCompanies = companies.filter(
      (company) =>
        company.disableShare &&
        company.raw?.isDeleted !== "Y" &&
        company.raw?.isSharing !== "N",
    ).length;

    if (hasDisabledShareCompanies && companyToShareCount == 0) {
      //alert("You've Already Shared Your Budget Today");

      localStorage.removeItem("companyList");

      console.log("companyToShareCount", companyToShareCount, resp);

      localStorage.setItem("alreadyShared", "Y");
      navigate("/share-success");
      setIsSharing(false);
      return;
    } else {
      localStorage.setItem("alreadyShared", "N");
    }

    // update balance amount
    if (latestSnapshot) {
      const payload = {
        ...latestSnapshot,
        balanceAmount: summary.balanceAmount,
      };

      // update existing snapshot data
      await httpPut<CustomerSharingResponse>(
        sharePageEndpoints.createCustomerLatestSnapshot(),
        payload,
      );
    } else {
      const payload = {
        balanceAmount: summary.balanceAmount,
        customerId: customerData.customerId,
      };

      await httpPost<CustomerSharingResponse>(
        sharePageEndpoints.createCustomerLatestSnapshot(),
        payload,
      );
    }

    let companyList = [];

    // send digital companies
    for (const company of companies) {
      try {
        if (
          company.raw?.shareNow == "Y" &&
          //!company.raw?.lastSharedDate &&
          company.raw?.isDeleted != "Y" &&
          company.raw?.isSharing != "N"
        ) {
          companyList.push(company.raw?.lenderName || company.raw?.companyName);
        }

        if (!company.isFp) {
          // const payload = {
          //   accountNumber: company.reference,
          //   customerId: customerData.customerId,
          //   lenderName: company.raw?.lenderName,
          //   orgId: 0,
          //   previousAccountNumber: company.reference,
          //   previousLenderName: company.raw?.lenderName,
          //   schemaName: company.raw?.schemaName,
          //   shareNow: company.raw?.shareNow,
          //   subDomain: company.raw?.subDomain,
          // };
          // await httpPost<CustomerSharingResponse>(
          //   sharePageEndpoints.insertCustomerSharingDetails(),
          //   payload
          // );
        }
      } catch (e) {
        femaCapture("sending_digital_company_error", e, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
      }
    }

    localStorage.setItem("companyList", companyList.join(";"));

    // UPDATE customer preferences for PayPlan and NDL (same API): the only place call time and contact choice are saved (POST first time, PUT after).
    // Order history: Mo (2025) ran 1. customer update (PATCH) -> 2. customer preferences (POST/PUT).
    // On Shyam's request (Oct 2026) it is reversed: 1. preferences -> 2. customer update, so the old data moves to history.
    // To revert, move this block back below the customer update (PATCH) further down.
    for (const company of companies) {
      const details = contactDetails[company.name];
      if (!CONTACT_COMPANIES.includes(company.name) || company.raw?.shareNow != "Y" || !details) continue;

      const fields = preferenceFields(details, company.name === "PayPlan");

      if (!company.ndlData?.isPreferencesLoaded) {
        await httpPost<ApiResponse>(customerEndpoints.customerPrefrences(), {
          customerId: customerData.customerId,
          ...fields,
          companyName: company.raw?.lenderName,
          sharingId: company.raw?.id,
          accountNumber: company.raw?.accountNumber,
        });
      } else {
        await httpPut<ApiResponse>(
          customerEndpoints.updateCustomerPrefrences(
            String(company.ndlData?.customerPrefrences?.id),
          ),
          {
            ...company.ndlData?.customerPrefrences,
            customerId: customerData.customerId,
            ...fields,
          },
        );
      }
    }

    // Finally update customer profile, even when no companies are selected.
    customerData.status = "SUBMIT";
    //customerData.isApiCall = "Y";
    customerData.isSubmitted = "Y";
    customerData.lenderList = getUniqueCompanieNames();
    customerData.submitDate = new Date(Date.now() + 3000).toISOString();

    delete customerData.cachedAt;

    const newCustomerData = await httpPatch<CustomerProfileData>(
      onboardingEndpoints.patchCustomer(customerData.id),
      customerData,
    );

    localStorage.setItem(
      "customerData",
      JSON.stringify(newCustomerData.data),
    );
    localStorage.setItem("submitDate", customerData.submitDate);

    navigate("/share-success");
  }

  // Visible label only. Sharing, matching, and saved names still use company.name.
  const companyDisplayName = (company: Company): string => {
    const description = findMasterLender(ieMstrLenderData, company.name)?.description?.trim();
    return description || company.name;
  };

  // One card per company: skips deleted and already-shared ones, then shows documents and company blocks
  const renderCompanyCard = (company: Company) => {
    if (company.raw?.lastSharedDate && company.raw?.isSharing != "Y") {
      return;
    } else if (company.raw?.isDeleted == "Y") {
      // if deleted condition
      return;
    }

    const isExpanded = expandedCompanies.includes(company.id);

    return (
      <Paper data-testid="pages-private-dashboard-render-company-card-container-action-1"
        key={company.id}
        id={`share-company-card-${company.id}`}
        elevation={0}
        sx={{
          p: isMobile ? 2 : 3,
          mb: 2,
          border:
            contactErrors[company.name]
              ? "2px solid #D32F2F"
              : `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          backgroundColor: "#fff",
          boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
        }}
      >
        <Box data-testid="pages-private-dashboard-render-company-card-container-action-2"
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 2,
            mb: 1,
          }}
        >
          <Avatar
            sx={{
              bgcolor: "#DBEAFE",
              color: theme.palette.primary.contrastText,
              width: isMobile ? 45 : 52,
              height: isMobile ? 45 : 52,
              borderRadius: 2,
            }}
          >
            <AccountBalanceIcon
              sx={{ fontSize: isMobile ? 22 : 26, color: "#2562EB" }}
            />
          </Avatar>

          <Box data-testid="pages-private-dashboard-render-company-card-container-action-3" sx={{ flex: 1 }}>
            <Box data-testid="pages-private-dashboard-render-company-card-container-action-4"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Box data-testid="pages-private-dashboard-render-company-card-container-action-5" sx={{ flex: 1, minWidth: 0 }}>
                <Typography data-testid="pages-private-dashboard-render-company-card-text-action-1"
                  variant={isMobile ? "body1" : "h5"}
                  data-private
                  data-clarity-mask="true"
                  sx={{ fontWeight: 400, color: "#0B3D3A", mb: 0, overflowWrap: "anywhere" }}
                >
                  {companyDisplayName(company)}{" "}
                  {false && company.raw?.source == "customer_profile" && (
                    <strong>(Your Referrer)</strong>
                  )}
                </Typography>
                <Typography data-testid="pages-private-dashboard-render-company-card-text-action-2"
                  variant="h6"
                  data-private
                  data-clarity-mask="true"
                  sx={{ color: "#ADB5BD", fontWeight: 400 }}
                >
                  {renderCompanyReference(company.reference)}
                </Typography>
              </Box>
              {((company.raw?.lastSharedDate || company.raw?.submitDate) &&
                company.disableShare &&
                company.raw?.isSharing == "Y") ||
              (company.raw?.submitDate &&
                company.disableShare &&
                company.raw?.isSharing == "Y" &&
                company.isFp) ? (
                <IconButton data-testid="pages-private-dashboard-render-company-card-btn-action-1"
                  size="small"
                  onClick={() => toggleDeleteCompanyDialog(company)}
                >
                  <DeleteIcon fontSize="small" sx={{ color: "#FF0000" }} />
                </IconButton>
              ) : company.raw?.lastSharedDate && company.raw?.submitDate ? (
                <IconButton data-testid="pages-private-dashboard-render-company-card-btn-action-2"
                  size="small"
                  onClick={() => toggleDeleteCompanyDialog(company)}
                >
                  <DeleteIcon fontSize="small" sx={{ color: "#FF0000" }} />
                </IconButton>
              ) : (!company.agentSubmitted ?
                <Box data-testid="pages-private-dashboard-render-company-card-container-action-6" display="flex" gap={1} alignItems="center">
                  <IconButton data-testid="pages-private-dashboard-render-company-card-btn-action-3"
                    size="small"
                    onClick={() => toggleEditCompanyDialog(company)}
                  >
                    <EditIcon fontSize="small" sx={{ color: "#ADB5BD" }} />
                  </IconButton>
                </Box> : null
              )}
            </Box>
          </Box>
        </Box>

        {company.address && (
          <Typography data-testid="pages-private-dashboard-render-company-card-text-address:"
            variant="h6"
            data-private
            data-clarity-mask="true"
            sx={{ color: theme.palette.text.secondary, mb: 2, fontWeight: 400 }}
          >
            Address: {company.address}
          </Typography>
        )}

        {/* {company.paymentOfferData && (
          <Typography
            variant="h6"
            sx={{ color: theme.palette.text.secondary, mb: 2, fontWeight: 400 }}
          >
            Repayment offer: £
            {company.paymentOfferData?.paymentOfferAmount || 0} • Payment day:{" "}
            {company.paymentOfferData?.paymentPreferredFrequency}
          </Typography>
        )} */}

        <Box data-testid="pages-private-dashboard-render-company-card-container-action-7">
          {!company.isPayPlanShared && (
          <Box data-testid="pages-private-dashboard-render-company-card-container-action-8"
            onClick={() => {
              if (!company.disableShare) {
                DashboardService.logButtonAction({
                  actionType: ActionType.BUTTON_CLICK,
                  actionName: `Toggle Share: ${company.name}`,
                  pageName: "Share",
                  subSection: "CompanyCard",
                }).catch(() => {});
                toggleCompanyShare(company);
              }
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 0.5,
              backgroundColor: company.disableShare
                ? "#9CA3AE"
                : company.raw?.shareNow == "Y"
                  ? "#2562EB"
                  : "#EFF6FF",
              border: `1px solid ${company.disableShare ? ThemeColours.borderGrey : "#BFDBFE"}`,
              borderRadius: 1,
              mb: isExpanded ? 2 : 0,
              cursor: company.disableShare ? "default" : "pointer",
            }}
          >
            <Typography data-testid="pages-private-dashboard-render-company-card-text-add-to-share"
              variant="h6"
              sx={{
                fontWeight: 500,
                color: company.disableShare
                  ? ThemeColours.borderGrey
                  : company.raw?.shareNow == "Y"
                    ? "#fff"
                    : "#1E40AF",
              }}
            >
              Add To Share
            </Typography>

            <CheckboxMui
              checked={company.raw?.shareNow === "Y"}
              onChange={() => toggleCompanyShare(company)}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              size="medium"
              disabled={company.disableShare}
              sx={{
                "&.Mui-checked": {
                  color: "#fff",
                },
              }}
            />
          </Box>
          )}

          {company.isPayPlanShared && (
            <Box data-testid="pages-private-dashboard-render-company-card-container-payplan-shared"
              sx={{
                mt: 2,
                p: 1.5,
                backgroundColor: ThemeColours.paleGreen,
                border: `1px solid ${ThemeColours.labelVar2}`,
                borderRadius: 1,
                color: ThemeColours.navy1,
              }}
            >
              <Typography data-testid="pages-private-dashboard-render-company-card-text-payplan-shared" variant="body1">
                {PAYPLAN_ALREADY_SHARED_TEXT}
              </Typography>
            </Box>
          )}

          {!company.isPayPlanShared && company.disableShare && (
            <Box data-testid="pages-private-dashboard-render-company-card-container-action-9"
              sx={{
                borderLeft: 4,
                borderColor: "#FF0F17",
                pl: 1,
                ml: 0.3,
                mb: 1.3,
                mt: 2,
                color: "#505A67",
              }}
            >
              <Typography data-testid="pages-private-dashboard-render-company-card-text-action-3" variant="body1">
                {company.notSupported ? `We are no longer able to send post to this company, if
                          you wish to share your budget you can download and
                          send via e-mail.` : `The same budget can only be shared once per day. Please try
                again tomorrow, or edit your budget to share again.`}{" "}
              </Typography>
            </Box>
          )}

          {/* Cadent Gas */}
          {company.name == "Cadent Gas" && company.raw?.shareNow == "Y" && (
            <>
              <Box data-testid="pages-private-dashboard-render-company-card-container-action-10"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 2,
                  pt: 1.5,
                  mt: 1.5,
                  pb: 1.5,
                  backgroundColor: "#E8EFEE",
                  border: "1px solid #165F54",
                  borderRadius: 1,
                  mb: 0,
                }}
              >
                <Stack data-testid="pages-private-dashboard-render-company-card-container-action-11">
                  <Typography data-testid="pages-private-dashboard-render-company-card-text-action-4" variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                    Got gas appliances at home? Click below to list them and get
                    the right support.
                  </Typography>

                  <Button data-testid="pages-private-dashboard-render-company-card-btn-view-or-edit-my-gas-appliances"
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<AddIcon sx={{ fontSize: 20 }} />}
                    sx={{
                      borderRadius: 1,
                      textTransform: "none",
                      fontWeight: 500,
                      py: 1,
                      boxShadow: "none",
                      maxWidth: 310,
                      backgroundColor: "#165F54",
                      fontSize: 16,
                    }}
                    onClick={() => {
                      setOpenCadentGasDialog(true);
                      setDeclarationCompany(company);
                    }}
                  >
                    View or edit my gas appliances
                  </Button>

                  {company.applianceCount > 0 && (
                    <Typography data-testid="pages-private-dashboard-render-company-card-text-added"
                      variant="h6"
                      sx={{
                        fontWeight: 300,
                        mb: 0,
                        mt: 1,
                        color: ThemeColours.labelVar2,
                      }}
                    >
                      {company.applianceCount}{" "}
                      {company.applianceCount > 1 ? "Appliances" : "Appliance"}{" "}
                      added
                    </Typography>
                  )}
                </Stack>
              </Box>
            </>
          )}

          {/* YWCT Declaration Not Completed */}
          {company.name == "Yorkshire Water Community Trust" &&
            company.raw?.shareNow === "Y" &&
            (company.declarationStatus == "not-completed" ||
              company.declarationStatus == "rejected") && (
              <>
                <Box data-testid="pages-private-dashboard-render-company-card-container-action-12"
                  sx={{
                    borderLeft: 4,
                    borderColor: "#165F54",
                    pl: 1,
                    ml: 0.3,
                    mb: 1.3,
                    mt: 2,
                    color: "text.secondary",
                  }}
                >
                  <Typography data-testid="pages-private-dashboard-render-company-card-text-action-5" variant="body1">
                    Need to Accept Consent to Share With Yorkshire Water
                    Community Trust
                  </Typography>
                </Box>

                <Box data-testid="pages-private-dashboard-render-company-card-container-action-13"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    pt: 1.5,
                    pb: 2,
                    backgroundColor: "#E8EFEE",
                    border: "1px solid #165F54",
                    borderRadius: 1,
                    mb: 0,
                  }}
                >
                  <Stack data-testid="pages-private-dashboard-render-company-card-container-action-14">
                    <Typography data-testid="pages-private-dashboard-render-company-card-text-action-6" variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                      To Share With Yorkshire Water Community Trust please{" "}
                      <u>Accept the consent</u>
                    </Typography>
                    <Typography data-testid="pages-private-dashboard-render-company-card-text-action-7" variant="h6" sx={{ fontWeight: 400, mb: 1 }}>
                      - Accept Required Declarations<br></br>- Upload Required
                      Documents
                      <br></br>- Accept the Consent<br></br>
                    </Typography>

                    <Button data-testid="pages-private-dashboard-render-company-card-btn-open-declaration-1"
                      variant="contained"
                      color="primary"
                      size="large"
                      endIcon={
                        <ArrowForwardIcon sx={{ ml: 2, fontSize: 22 }} />
                      }
                      sx={{
                        borderRadius: 1,
                        textTransform: "none",
                        fontWeight: 500,
                        py: 1.3,
                        boxShadow: "none",
                        width: 220,
                        backgroundColor: "#165F54",
                        fontSize: 16,
                      }}
                      onClick={() => {
                        setOpenYWCTDeclarationDialog(true);
                        setDeclarationCompany(company);
                      }}
                    >
                      Open Declaration
                    </Button>
                  </Stack>
                </Box>
              </>
            )}

          {/* YWCT Declaration Accepted */}
          {company.name == "Yorkshire Water Community Trust" &&
            company.raw?.shareNow === "Y" &&
            company.declarationStatus == "accepted" && (
              <Box data-testid="pages-private-dashboard-render-company-card-container-action-15"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 2,
                  pt: 1.5,
                  pb: 2,
                  backgroundColor: "#E8EFEE",
                  border: "1px solid #165F54",
                  borderRadius: 1,
                  mb: 0,
                  mt: 1.5,
                }}
              >
                <Stack data-testid="pages-private-dashboard-render-company-card-container-action-16">
                  <Typography data-testid="pages-private-dashboard-render-company-card-text-action-8" variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                    You are all set to share your information with Yorkshire
                    Water Community Trust
                  </Typography>
                  <Typography data-testid="pages-private-dashboard-render-company-card-text-action-9" variant="h6" sx={{ fontWeight: 400, mb: 0.5 }}>
                    <Link data-testid="pages-private-dashboard-render-company-card-link-click-here-1"
                      sx={{ color: "#000", cursor: "pointer" }}
                      onClick={() => {
                        setOpenYWCTDeclarationDialog(true);
                        setDeclarationCompany(company);
                      }}
                    >
                      Click here
                    </Link>
                    ‎ if you wish to agree to the declaration and share your
                    information with Yorkshire Water Community Trust
                  </Typography>
                </Stack>
              </Box>
            )}

          {/* YWCT Declaration Rejected */}
          {company.name == "Yorkshire Water Community Trust" &&
            company.declarationStatus == "rejected" &&
            company.raw?.shareNow === "N" && (
              <Box data-testid="pages-private-dashboard-render-company-card-container-action-17"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 2,
                  pt: 1.5,
                  pb: 2,
                  backgroundColor: "#E8EFEE",
                  border: "1px solid #165F54",
                  borderRadius: 1,
                  mb: 0,
                  mt: 1.5,
                }}
              >
                <Stack data-testid="pages-private-dashboard-render-company-card-container-action-18">
                  <Typography data-testid="pages-private-dashboard-render-company-card-text-action-10" variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                    You have rejected the declaration, therefore, your
                    information will not be shared with Yorkshire Water
                    Community Trust
                  </Typography>
                  <Typography data-testid="pages-private-dashboard-render-company-card-text-action-11" variant="h6" sx={{ fontWeight: 400, mb: 0.5 }}>
                    <Link data-testid="pages-private-dashboard-render-company-card-link-click-here-2"
                      sx={{ color: "#000", cursor: "pointer" }}
                      onClick={() => {
                        setOpenYWCTDeclarationDialog(true);
                        setDeclarationCompany(company);
                      }}
                    >
                      Click here
                    </Link>
                    ‎ if you wish to agree to the declaration and share your
                    information with Yorkshire Water Community Trust
                  </Typography>
                </Stack>
              </Box>
            )}

          {/* PayPlan and NDL: shared contact card (form, saved view, call time), shown once ticked to share */}
          {CONTACT_COMPANIES.includes(company.name) && company.raw?.shareNow === "Y" && (
            <ContactPreferenceCard
              companyName={company.name}
              details={contactDetails[company.name]}
              defaultMobile={profileContact.mobile}
              defaultEmail={profileContact.email}
              // Hidden until customer asks for Payplan (was part of Oct 2026 patch)
              showWhatsApp={false}
              // NDL has no "Anytime": it always needs one of Morning, Afternoon, Evening
              allowAnytime={company.name === "PayPlan"}
              // NDL's info icon opens its info dialog
              {...(company.name === "National Debtline" && {
                onInfoClick: () => toggleInfoDialog({ ...company, name: "NDL-INFO" }),
              })}
              onSave={(details) => handleContactSave(company.name, details)}
              onCallTimeChange={(callTime: CallTime | null) => {
                const saved = contactDetails[company.name];
                if (saved) saveContactChoice(company.name, { ...saved, callTime }); // remember the call time
                setContactDetails((prev) => {
                  const current = prev[company.name];
                  return current ? { ...prev, [company.name]: { ...current, callTime } } : prev;
                });
                setContactErrors((prev) => ({ ...prev, [company.name]: false }));
              }}
              onErrorChange={(hasError) => setContactErrors((prev) => ({ ...prev, [company.name]: hasError }))}
              onEditingChange={(editing) => setContactEditing((prev) => ({ ...prev, [company.name]: editing }))}
            />
          )}

          {/* Fee line after Add To Share and any contact form, for every company listed in SHARE_FEE_LINES */}
          {Object.entries(SHARE_FEE_LINES).map(([key, text]) =>
            company.name.toUpperCase().includes(key) ? <FeeLine key={key} text={text} /> : null,
          )}

          {/* UU Declaration Not Completed */}
          {company.name == "United Utilities" &&
            company.raw?.shareNow === "Y" &&
            (company.declarationStatus == "not-completed" ||
              company.declarationStatus == "rejected") && (
              <>
                <Box data-testid="pages-private-dashboard-render-company-card-container-action-21"
                  sx={{
                    borderLeft: 4,
                    borderColor: "#165F54",
                    pl: 1,
                    ml: 0.3,
                    mb: 1.3,
                    mt: 2,
                    color: "text.secondary",
                  }}
                >
                  <Typography data-testid="pages-private-dashboard-render-company-card-text-action-13" variant="body1">
                    Need to Accept Consent to Share With United Utilities
                  </Typography>
                </Box>

                <Box data-testid="pages-private-dashboard-render-company-card-container-action-22"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    pt: 1.5,
                    pb: 2,
                    backgroundColor: "#E8EFEE",
                    border: "1px solid #165F54",
                    borderRadius: 1,
                    mb: 0,
                  }}
                >
                  <Stack data-testid="pages-private-dashboard-render-company-card-container-action-23">
                    <Typography data-testid="pages-private-dashboard-render-company-card-text-to-share-with-united-utilities-please" variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                      To Share With United Utilities please{" "}
                      <u>Accept the consent</u>
                    </Typography>
                    <Typography data-testid="pages-private-dashboard-render-company-card-text-action-14" variant="h6" sx={{ fontWeight: 400, mb: 1 }}>
                      - Accept Required Declarations<br></br>- Upload Required
                      Documents
                      <br></br>- Accept the Consent<br></br>
                    </Typography>

                    <Button data-testid="pages-private-dashboard-render-company-card-btn-open-declaration-2"
                      variant="contained"
                      color="primary"
                      size="large"
                      endIcon={
                        <ArrowForwardIcon sx={{ ml: 2, fontSize: 22 }} />
                      }
                      sx={{
                        borderRadius: 1,
                        textTransform: "none",
                        fontWeight: 500,
                        py: 1.3,
                        boxShadow: "none",
                        width: 220,
                        backgroundColor: "#165F54",
                        fontSize: 16,
                      }}
                      onClick={() => {
                        setOpenUUDeclarationDialog(true);
                        setDeclarationCompany(company);
                      }}
                    >
                      Open Declaration
                    </Button>
                  </Stack>
                </Box>
              </>
            )}

          {/* UU Declaration Accepted */}
          {company.name == "United Utilities" &&
            company.raw?.shareNow === "Y" &&
            company.declarationStatus == "accepted" && (
              <Box data-testid="pages-private-dashboard-render-company-card-container-action-24"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 2,
                  pt: 1.5,
                  pb: 2,
                  backgroundColor: "#E8EFEE",
                  border: "1px solid #165F54",
                  borderRadius: 1,
                  mb: 0,
                  mt: 1.5,
                }}
              >
                <Stack data-testid="pages-private-dashboard-render-company-card-container-action-25">
                  <Typography data-testid="pages-private-dashboard-render-company-card-text-action-15" variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                    You are all set to share your information with United
                    Utilities
                  </Typography>
                  <Typography data-testid="pages-private-dashboard-render-company-card-text-action-16" variant="h6" sx={{ fontWeight: 400, mb: 0.5 }}>
                    <Link data-testid="pages-private-dashboard-render-company-card-link-click-here-3"
                      sx={{ color: "#000", cursor: "pointer" }}
                      onClick={() => {
                        setOpenUUDeclarationDialog(true);
                        setDeclarationCompany(company);
                      }}
                    >
                      Click here
                    </Link>
                    ‎ if you wish to agree to the declaration and share your
                    information with United Utilities
                  </Typography>
                </Stack>
              </Box>
            )}

          {/* UU Declaration Rejected */}
          {company.name == "United Utilities" &&
            company.declarationStatus == "rejected" &&
            company.raw?.shareNow === "N" && (
              <Box data-testid="pages-private-dashboard-render-company-card-container-action-26"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 2,
                  pt: 1.5,
                  pb: 2,
                  backgroundColor: "#E8EFEE",
                  border: "1px solid #165F54",
                  borderRadius: 1,
                  mb: 0,
                  mt: 1.5,
                }}
              >
                <Stack data-testid="pages-private-dashboard-render-company-card-container-action-27">
                  <Typography data-testid="pages-private-dashboard-render-company-card-text-action-17" variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                    You have rejected the declaration, therefore, your
                    information will not be shared with United Utilities
                  </Typography>
                  <Typography data-testid="pages-private-dashboard-render-company-card-text-action-18" variant="h6" sx={{ fontWeight: 400, mb: 0.5 }}>
                    <Link data-testid="pages-private-dashboard-render-company-card-link-click-here-4"
                      sx={{ color: "#000", cursor: "pointer" }}
                      onClick={() => {
                        setOpenUUDeclarationDialog(true);
                        setDeclarationCompany(company);
                      }}
                    >
                      Click here
                    </Link>
                    ‎ if you wish to agree to the declaration and share your
                    information with United Utilities
                  </Typography>
                </Stack>
              </Box>
            )}

          {company.name == "CDER Group Limited" &&
            company.raw?.shareNow === "Y" && (
              <>
                <Box data-testid="pages-private-dashboard-render-company-card-container-action-28"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    pt: 1.5,
                    pb: 2,
                    mt: 1.5,
                    backgroundColor: "#E8EFEE",
                    border: "1px solid #165F54",
                    borderRadius: 1,
                    mb: 0,
                  }}
                >
                  <Stack data-testid="pages-private-dashboard-render-company-card-container-action-29">
                    <Typography data-testid="pages-private-dashboard-render-company-card-text-confirmation" variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                      Confirmation
                    </Typography>
                    <Typography data-testid="pages-private-dashboard-render-company-card-text-action-19" variant="h6" sx={{ fontWeight: 400, mb: 1 }}>
                      Please upload evidence of your income and any
                      vulnerabilities. Without this information, CDER Group are
                      unable to consider any arrangement on your account.
                    </Typography>

                    <Button data-testid="pages-private-dashboard-render-company-card-btn-add-document"
                      variant="contained"
                      color="primary"
                      size="large"
                      startIcon={<AddIcon sx={{ fontSize: 20 }} />}
                      sx={{
                        borderRadius: 1,
                        textTransform: "none",
                        fontWeight: 500,
                        py: 1,
                        boxShadow: "none",
                        maxWidth: 190,
                        backgroundColor: "#165F54",
                        fontSize: 16,
                      }}
                      onClick={() => {
                        navigate("/documents?ref=cder");
                      }}
                    >
                      Add Document
                    </Button>
                  </Stack>
                </Box>
              </>
            )}

          {company.name != "Cadent Gas" &&
            company.raw?.shareNow === "Y" &&
            company.documents.length > 0 && (
              <Box data-testid="pages-private-dashboard-render-company-card-container-action-30" sx={{ mt: 2 }}>
                <Typography data-testid="pages-private-dashboard-render-company-card-text-select-documents-to-share"
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    mb: 1.5,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  Select Documents To Share
                  <InfoIcon
                    sx={{
                      color: "#165F54",
                      fontSize: 14,
                      ml: 0.7,
                      cursor: "pointer",
                      position: "relative",
                      bottom: 1,
                    }}
                    onClick={() => {
                      toggleInfoDialog(company);
                    }}
                  />
                </Typography>

                <Stack data-testid="pages-private-dashboard-render-company-card-container-action-31" spacing={2}>
                  {company.documents.map((doc) => (
                    <Box data-testid="pages-private-dashboard-render-company-card-container-action-32"
                      key={doc.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        p: 1.5,
                        backgroundColor: "#fff",
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                      }}
                    >
                      <Box data-testid="pages-private-dashboard-render-company-card-container-action-33"
                        sx={{
                          width: 40,
                          height: 40,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#DCFCE7",
                          borderRadius: 2,
                        }}
                      >
                        <DescriptionOutlinedIcon sx={{ color: "#165F54" }} />
                      </Box>

                      <Box data-testid="pages-private-dashboard-render-company-card-container-action-34" sx={{ flex: 1, minWidth: 0 }}>
                        <Typography data-testid="pages-private-dashboard-render-company-card-text-action-20"
                          variant="h6"
                          sx={{
                            fontWeight: 500,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {doc.name}
                        </Typography>

                        <Typography data-testid="pages-private-dashboard-render-company-card-text-•-uploaded"
                          variant="body2"
                          sx={{ color: theme.palette.text.secondary }}
                        >
                          {doc.size} • Uploaded {doc.uploadedDate}
                        </Typography>
                      </Box>

                      <Checkbox
                        checked={company.selectedDocuments.includes(doc.id)}
                        onChange={() =>
                          handleDocumentToggle(company.id, doc.id)
                        }
                        size="medium"
                        disabled={company.raw?.shareNow !== "Y"}
                      />
                    </Box>
                  ))}
                </Stack>
                {company.raw?.shareNow !== "Y" && (
                  <Typography data-testid="pages-private-dashboard-render-company-card-text-action-21"
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      mt: 1,
                      display: "block",
                    }}
                  >
                    Turn on “Add To Share” to choose which documents to include.
                  </Typography>
                )}
              </Box>
            )}

          {company.raw?.submitDate && !company.agentSubmitted && ( 
            <Box data-testid="pages-private-dashboard-render-company-card-container-action-35" sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
              <AccessTimeFilledIcon sx={{ fontSize: 16, color: "#6B727F" }} />
              <Typography data-testid="pages-private-dashboard-render-company-card-text-last-shared-on" variant="body2" sx={{ color: "#6B727F" }}>
                Last shared on{" "}
                {company.raw?.submitDate
                  ? formatDate(company.raw?.submitDate)
                  : "-"}
              </Typography>
            </Box>
          )}

          {false && company.documents.length === 0 && (
            <Box data-testid="pages-private-dashboard-render-company-card-container-action-36"
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: theme.palette.warning.light + "20",
                borderRadius: 1,
              }}
            >
              <Typography data-testid="pages-private-dashboard-render-company-card-text-action-22"
                variant="body1"
                sx={{ color: theme.palette.warning.dark }}
              >
                No documents available to share with this company.
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
    );
  };

  const renderCompanySection = (
    title: string,
    companies: Company[],
    ref?: React.RefObject<HTMLDivElement>,
    id?: string,
  ) => {
    if (companies.length === 0) return null;

    return (
      <Box data-testid="pages-private-dashboard-render-company-section-container-action"
        ref={ref || null}
        id={id || undefined}
        sx={{ mb: 4, scrollMarginTop: "100px" }}
      >
        {/* PayPlan first, every other company keeps its current order (PB-330 AC1) */}
        {[...companies]
          .sort((a, b) => Number(b.name === "PayPlan") - Number(a.name === "PayPlan"))
          .map(renderCompanyCard)}
      </Box>
    );
  };

  const renderDeclarationModal = () => (
    <Dialog data-testid="pages-private-dashboard-render-declaration-modal-modal-action"
      open={declarationOpen}
      onClose={handleDeclarationClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      TransitionComponent={Transition as any}
      sx={{
        "& .MuiDialog-container": {
          alignItems: { xs: "stretch", md: "flex-end" },
        },
        "& .MuiPaper-root": {
          m: 0,
          width: "100%",
          maxWidth: { md: 600 },
          borderTopLeftRadius: { xs: 0, md: 16 },
          borderTopRightRadius: { xs: 0, md: 16 },
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        },
      }}
    >
      <DialogTitle data-testid="pages-private-dashboard-render-declaration-modal-header-action"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography data-testid="pages-private-dashboard-render-declaration-modal-text-upload-utilities-declaration" variant="h6" sx={{ fontWeight: 600 }}>
          Upload Utilities Declaration
        </Typography>
        <IconButton data-testid="pages-private-dashboard-render-declaration-modal-btn-handle-declaration-close" onClick={handleDeclarationClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Stack data-testid="pages-private-dashboard-render-declaration-modal-container-action-1" spacing={3}>
          {/* Confirming Household Status */}
          <Box data-testid="pages-private-dashboard-render-declaration-modal-container-action-2">
            <Typography data-testid="pages-private-dashboard-render-declaration-modal-text-confirming-household-status" variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Confirming household status
            </Typography>
            <Typography data-testid="pages-private-dashboard-render-declaration-modal-text-action-1"
              variant="body2"
              sx={{ color: theme.palette.text.secondary, mb: 2 }}
            >
              I can confirm this is a Single Adult household and provide you
              with the support you need.
            </Typography>
            <Box data-testid="pages-private-dashboard-render-declaration-modal-container-action-3" sx={{ display: "flex", gap: 2 }}>
              <Chip data-testid="pages-private-dashboard-render-declaration-modal-chip-yes-1"
                label="Yes"
                onClick={() =>
                  setHouseholdDeclaration((prev) => ({
                    ...prev,
                    status: "yes",
                  }))
                }
                sx={{
                  backgroundColor:
                    householdDeclaration.status === "yes"
                      ? theme.palette.success.main
                      : "transparent",
                  color:
                    householdDeclaration.status === "yes"
                      ? "#fff"
                      : theme.palette.text.primary,
                  border: `1px solid ${householdDeclaration.status === "yes" ? theme.palette.success.main : theme.palette.divider}`,
                  fontWeight: 500,
                }}
              />
              <Chip data-testid="pages-private-dashboard-render-declaration-modal-chip-no-1"
                label="No"
                onClick={() =>
                  setHouseholdDeclaration((prev) => ({ ...prev, status: "no" }))
                }
                sx={{
                  backgroundColor:
                    householdDeclaration.status === "no"
                      ? theme.palette.error.main
                      : "transparent",
                  color:
                    householdDeclaration.status === "no"
                      ? "#fff"
                      : theme.palette.text.primary,
                  border: `1px solid ${householdDeclaration.status === "no" ? theme.palette.error.main : theme.palette.divider}`,
                  fontWeight: 500,
                }}
              />
            </Box>
          </Box>

          {/* Confirming Household Circumstances */}
          <Box data-testid="pages-private-dashboard-render-declaration-modal-container-action-4">
            <Typography data-testid="pages-private-dashboard-render-declaration-modal-text-confirming-household-circumstances" variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Confirming household circumstances
            </Typography>
            <Typography data-testid="pages-private-dashboard-render-declaration-modal-text-action-2"
              variant="body2"
              sx={{ color: theme.palette.text.secondary, mb: 2 }}
            >
              I can confirm there are no changes to my household and I can
              provide you with the support you need.
            </Typography>
            <Box data-testid="pages-private-dashboard-render-declaration-modal-container-action-5" sx={{ display: "flex", gap: 2 }}>
              <Chip data-testid="pages-private-dashboard-render-declaration-modal-chip-yes-2"
                label="Yes"
                onClick={() =>
                  setHouseholdDeclaration((prev) => ({
                    ...prev,
                    circumstances: "yes",
                  }))
                }
                sx={{
                  backgroundColor:
                    householdDeclaration.circumstances === "yes"
                      ? theme.palette.success.main
                      : "transparent",
                  color:
                    householdDeclaration.circumstances === "yes"
                      ? "#fff"
                      : theme.palette.text.primary,
                  border: `1px solid ${householdDeclaration.circumstances === "yes" ? theme.palette.success.main : theme.palette.divider}`,
                  fontWeight: 500,
                }}
              />
              <Chip data-testid="pages-private-dashboard-render-declaration-modal-chip-no-2"
                label="No"
                onClick={() =>
                  setHouseholdDeclaration((prev) => ({
                    ...prev,
                    circumstances: "no",
                  }))
                }
                sx={{
                  backgroundColor:
                    householdDeclaration.circumstances === "no"
                      ? theme.palette.error.main
                      : "transparent",
                  color:
                    householdDeclaration.circumstances === "no"
                      ? "#fff"
                      : theme.palette.text.primary,
                  border: `1px solid ${householdDeclaration.circumstances === "no" ? theme.palette.error.main : theme.palette.divider}`,
                  fontWeight: 500,
                }}
              />
            </Box>
          </Box>

          {/* Previous Credit */}
          <Box data-testid="pages-private-dashboard-render-declaration-modal-container-action-6">
            <Typography data-testid="pages-private-dashboard-render-declaration-modal-text-previous-credit" variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Previous Credit
            </Typography>
            <Typography data-testid="pages-private-dashboard-render-declaration-modal-text-action-3"
              variant="body2"
              sx={{ color: theme.palette.text.secondary, mb: 2 }}
            >
              Is there a change in the household in charge of utility payments
              or any previous credit with this company?
            </Typography>
            <Box data-testid="pages-private-dashboard-render-declaration-modal-container-action-7" sx={{ display: "flex", gap: 2 }}>
              <Chip data-testid="pages-private-dashboard-render-declaration-modal-chip-yes-3"
                label="Yes"
                onClick={() =>
                  setHouseholdDeclaration((prev) => ({
                    ...prev,
                    previousCredit: "yes",
                  }))
                }
                sx={{
                  backgroundColor:
                    householdDeclaration.previousCredit === "yes"
                      ? theme.palette.success.main
                      : "transparent",
                  color:
                    householdDeclaration.previousCredit === "yes"
                      ? "#fff"
                      : theme.palette.text.primary,
                  border: `1px solid ${householdDeclaration.previousCredit === "yes" ? theme.palette.success.main : theme.palette.divider}`,
                  fontWeight: 500,
                }}
              />
              <Chip data-testid="pages-private-dashboard-render-declaration-modal-chip-no-3"
                label="No"
                onClick={() =>
                  setHouseholdDeclaration((prev) => ({
                    ...prev,
                    previousCredit: "no",
                  }))
                }
                sx={{
                  backgroundColor:
                    householdDeclaration.previousCredit === "no"
                      ? theme.palette.error.main
                      : "transparent",
                  color:
                    householdDeclaration.previousCredit === "no"
                      ? "#fff"
                      : theme.palette.text.primary,
                  border: `1px solid ${householdDeclaration.previousCredit === "no" ? theme.palette.error.main : theme.palette.divider}`,
                  fontWeight: 500,
                }}
              />
            </Box>
          </Box>

          {/* Household Financial Position */}
          <Box data-testid="pages-private-dashboard-render-declaration-modal-container-action-8">
            <Typography data-testid="pages-private-dashboard-render-declaration-modal-text-household-financial-position" variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Household Financial Position
            </Typography>
            <Typography data-testid="pages-private-dashboard-render-declaration-modal-text-action-4"
              variant="body2"
              sx={{ color: theme.palette.text.secondary, mb: 2 }}
            >
              Do you donate to the property either in neutral circumstances or
              by rental property?
            </Typography>
            <Stack data-testid="pages-private-dashboard-render-declaration-modal-container-action-9" direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip data-testid="pages-private-dashboard-render-declaration-modal-chip-yes-4"
                label="Yes"
                onClick={() =>
                  setHouseholdDeclaration((prev) => ({
                    ...prev,
                    financialPositionChange: "yes",
                  }))
                }
                sx={{
                  backgroundColor:
                    householdDeclaration.financialPositionChange === "yes"
                      ? theme.palette.success.main
                      : "transparent",
                  color:
                    householdDeclaration.financialPositionChange === "yes"
                      ? "#fff"
                      : theme.palette.text.primary,
                  border: `1px solid ${householdDeclaration.financialPositionChange === "yes" ? theme.palette.success.main : theme.palette.divider}`,
                  fontWeight: 500,
                  mb: 1,
                }}
              />
              <Chip data-testid="pages-private-dashboard-render-declaration-modal-chip-no-4"
                label="No"
                onClick={() =>
                  setHouseholdDeclaration((prev) => ({
                    ...prev,
                    financialPositionChange: "no",
                  }))
                }
                sx={{
                  backgroundColor:
                    householdDeclaration.financialPositionChange === "no"
                      ? theme.palette.error.main
                      : "transparent",
                  color:
                    householdDeclaration.financialPositionChange === "no"
                      ? "#fff"
                      : theme.palette.text.primary,
                  border: `1px solid ${householdDeclaration.financialPositionChange === "no" ? theme.palette.error.main : theme.palette.divider}`,
                  fontWeight: 500,
                  mb: 1,
                }}
              />
              <Chip data-testid="pages-private-dashboard-render-declaration-modal-chip-not-applicable"
                label="Not Applicable"
                onClick={() =>
                  setHouseholdDeclaration((prev) => ({
                    ...prev,
                    financialPositionChange: "not-applicable",
                  }))
                }
                sx={{
                  backgroundColor:
                    householdDeclaration.financialPositionChange ===
                    "not-applicable"
                      ? theme.palette.info.main
                      : "transparent",
                  color:
                    householdDeclaration.financialPositionChange ===
                    "not-applicable"
                      ? "#fff"
                      : theme.palette.text.primary,
                  border: `1px solid ${householdDeclaration.financialPositionChange === "not-applicable" ? theme.palette.info.main : theme.palette.divider}`,
                  fontWeight: 500,
                  mb: 1,
                }}
              />
            </Stack>
          </Box>

          <Divider />

          {/* File Upload Section */}
          <Box data-testid="pages-private-dashboard-render-declaration-modal-container-action-10">
            <Typography data-testid="pages-private-dashboard-render-declaration-modal-text-drag-&-drop-files-here" variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Drag & drop files here
            </Typography>
            <Typography data-testid="pages-private-dashboard-render-declaration-modal-text-or-tap-to-browse-your-device"
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                mb: 2,
                display: "block",
              }}
            >
              or tap to browse your device
            </Typography>

            <Box data-testid="pages-private-dashboard-render-declaration-modal-container-action-11"
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => {
                const element = document.getElementById("file-upload-input");
                if (element) {
                  (element as HTMLInputElement).click();
                }
              }}
              sx={{
                border: `2px dashed ${isDragging ? theme.palette.primary.main : theme.palette.divider}`,
                borderRadius: 2,
                p: 4,
                textAlign: "center",
                cursor: "pointer",
                backgroundColor: isDragging
                  ? theme.palette.action.hover
                  : theme.palette.background.default,
                transition: "all 0.2s",
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              <CloudUploadIcon
                sx={{
                  fontSize: 48,
                  color: theme.palette.text.secondary,
                  mb: 1,
                }}
              />
              <Typography data-testid="pages-private-dashboard-render-declaration-modal-text-drop-files-here-or-click-to-browse"
                variant="body2"
                sx={{ color: theme.palette.text.secondary }}
              >
                Drop files here or click to browse
              </Typography>
              <Typography data-testid="pages-private-dashboard-render-declaration-modal-text-supported-formats:-pdf,-jpg,-png-(max-10mb)"
                variant="caption"
                sx={{ color: theme.palette.text.disabled }}
              >
                Supported formats: PDF, JPG, PNG (Max 10MB)
              </Typography>
            </Box>

            <input data-testid="pages-private-dashboard-render-declaration-modal-input-file-upload-input"
              id="file-upload-input"
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png"
              style={{ display: "none" }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleFileUpload(e.target.files)
              }
            />
          </Box>

          {/* Uploaded Files List */}
          {uploadedFiles.length > 0 && (
            <Box data-testid="pages-private-dashboard-render-declaration-modal-container-action-12">
              <Typography data-testid="pages-private-dashboard-render-declaration-modal-text-document-list-required" variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Document List Required
              </Typography>
              <Stack data-testid="pages-private-dashboard-render-declaration-modal-container-action-13" spacing={1}>
                {uploadedFiles.map((file) => (
                  <Box data-testid="pages-private-dashboard-render-declaration-modal-container-action-14"
                    key={file.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      p: 1.5,
                      backgroundColor: theme.palette.success.light + "20",
                      border: `1px solid ${theme.palette.success.light}`,
                      borderRadius: 1,
                    }}
                  >
                    <Box data-testid="pages-private-dashboard-render-declaration-modal-container-action-15"
                      sx={{
                        width: 40,
                        height: 40,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: theme.palette.success.light + "40",
                        borderRadius: 1,
                      }}
                    >
                      <DescriptionIcon
                        sx={{ color: theme.palette.success.main }}
                      />
                    </Box>

                    <Box data-testid="pages-private-dashboard-render-declaration-modal-container-action-16" sx={{ flex: 1 }}>
                      <Typography data-testid="pages-private-dashboard-render-declaration-modal-text-action-5" variant="body2" sx={{ fontWeight: 500 }}>
                        {file.name}
                      </Typography>
                      <Typography data-testid="pages-private-dashboard-render-declaration-modal-text-•-uploaded"
                        variant="caption"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        {formatFileSize(file.size)} • Uploaded{" "}
                        {file.uploadDate.toLocaleDateString()}
                      </Typography>
                    </Box>

                    <CheckCircleIcon
                      sx={{ color: theme.palette.success.main, fontSize: 20 }}
                    />

                    <IconButton data-testid="pages-private-dashboard-render-declaration-modal-btn-action"
                      size="small"
                      onClick={() => handleRemoveFile(file.id)}
                      sx={{ color: theme.palette.error.main }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {/* Warning Message */}
          <Box data-testid="pages-private-dashboard-render-declaration-modal-container-action-17"
            sx={{
              display: "flex",
              gap: 1,
              p: 2,
              backgroundColor: theme.palette.error.light + "20",
              borderRadius: 1,
              border: `1px solid ${theme.palette.error.light}`,
            }}
          >
            <WarningIcon
              sx={{ color: theme.palette.error.main, fontSize: 20, mt: 0.5 }}
            />
            <Typography data-testid="pages-private-dashboard-render-declaration-modal-text-action-6"
              variant="caption"
              sx={{ color: theme.palette.error.dark, lineHeight: 1.5 }}
            >
              You will not share this same E with companies that you're not
              already shared it with in the past 24 hours
            </Typography>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{ p: 3, borderTop: `1px solid ${theme.palette.divider}` }}
      >
        <Box data-testid="pages-private-dashboard-render-declaration-modal-container-handle-declaration-submit"
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            py: 1.5,
            backgroundColor: theme.palette.primary.main,
            color: "#fff",
            borderRadius: 2,
            cursor: "pointer",
            fontWeight: 600,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
            },
          }}
          onClick={handleDeclarationSubmit}
        >
          Save & continue
        </Box>
      </DialogActions>
    </Dialog>
  );

  const renderAddCompanyDialog = () => {
    return (
      <Dialog data-testid="pages-private-dashboard-render-add-company-dialog-modal-action"
        open={addCompanyOpen}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            ...(isMobile && {
              margin: 0,
              width: "100%",
              maxWidth: "100%",
              maxHeight: "90vh",
              borderRadius: "16px 16px 0 0",
              position: "fixed",
              bottom: 0,
            }),
            ...(!isMobile && {
              maxHeight: "90vh",
              padding: "0px 0px 20px 0px",
            }),
          },
        }}
        TransitionComponent={Transition as any}
        BackdropComponent={Backdrop}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
          },
        }}
      >
        <AddCompanyDialog
          onClose={() => {
            setAddCompanyOpen(false);
          }}
          balanceAmount={summary.balanceAmount}
          ieMstrLenderData={ieMstrLenderData}
          digitalLenders={digitalLenders}
          setCompanies={
            setCompanies as React.Dispatch<React.SetStateAction<any[]>>
          }
          setCompanyShareLastUpdateDate={setCompanyShareLastUpdateDate}
          freepostCreditorNamesData={freepostCreditorNamesData}
          declarationData={declarationData}
          companies={companies as any[]}
          source="share-page"
        />
      </Dialog>
    );
  };

  const renderShareHistoryDialog = () => {
    return (
      <Dialog data-testid="pages-private-dashboard-render-share-history-dialog-modal-action"
        open={shareHistoryOpen}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            ...(isMobile && {
              margin: 0,
              width: "100%",
              maxWidth: "100%",
              maxHeight: "90vh",
              borderRadius: "16px 16px 0 0",
              position: "fixed",
              bottom: 0,
            }),
            ...(!isMobile && {
              height: "100vh",
            }),
          },
        }}
        TransitionComponent={Transition as any}
        BackdropComponent={Backdrop}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
          },
        }}
      >
        <ShareHistoryDialog
          onClose={() => {
            setShareHistoryOpen(false);
          }}
          shareBudgetsLogic={shareBudgetsLogic as unknown as () => boolean}
          companies={companies}
          setCompanies={setCompanies}
          eligibleAspConditions={eligibleAspConditions}
          lenders={ieMstrLenderData}
          onIneligibleAspCompany={(companyName: string) => {
            setIneligibleCompanies([companyName]);
            setUpdatedRecommendationsModalSource("history");
            setShowUpdatedRecommendationsModal(true);
          }}
        />
      </Dialog>
    );
  };

  const renderUUDeclarationDialog = () => {
    return (
      <Dialog data-testid="pages-private-dashboard-render-uudeclaration-dialog-modal-action"
        open={openUUDeclarationDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            ...(isMobile && {
              margin: 0,
              width: "100%",
              maxWidth: "100%",
              maxHeight: "90vh",
              borderRadius: "16px 16px 0 0",
              position: "fixed",
              bottom: 0,
            }),
            ...(!isMobile && {
              height: "100vh",
            }),
          },
        }}
        TransitionComponent={Transition as any}
        BackdropComponent={Backdrop}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
          },
        }}
      >
        <UUDeclarationDialog
          onClose={() => {
            setOpenUUDeclarationDialog(false);
          }}
          handleDocumentToggle={handleDocumentToggle}
          company={declarationCompany}
          setCompanies={setCompanies}
        />
      </Dialog>
    );
  };

  const renderYWCTDeclarationDialog = () => {
    return (
      <Dialog data-testid="pages-private-dashboard-render-ywctdeclaration-dialog-modal-action"
        open={openYWCTDeclarationDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            ...(isMobile && {
              margin: 0,
              width: "100%",
              maxWidth: "100%",
              maxHeight: "90vh",
              borderRadius: "16px 16px 0 0",
              position: "fixed",
              bottom: 0,
            }),
            ...(!isMobile && {
              height: "100vh",
            }),
          },
        }}
        TransitionComponent={Transition as any}
        BackdropComponent={Backdrop}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
          },
        }}
      >
        <YWCTDeclarationDialog
          onClose={() => {
            setOpenYWCTDeclarationDialog(false);
          }}
          company={declarationCompany}
          setCompanies={setCompanies}
          toggleCompanyShare={toggleCompanyShare}
        />
      </Dialog>
    );
  };

  const renderCadentGasDialog = () => {
    return (
      <Dialog data-testid="pages-private-dashboard-render-cadent-gas-dialog-modal-action"
        open={openCadentGasDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            ...(isMobile && {
              margin: 0,
              width: "100%",
              maxWidth: "100%",
              maxHeight: "90vh",
              borderRadius: "16px 16px 0 0",
              position: "fixed",
              bottom: 0,
            }),
            ...(!isMobile && {
              height: "100vh",
            }),
          },
        }}
        TransitionComponent={Transition as any}
        BackdropComponent={Backdrop}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
          },
        }}
      >
        <CadentGasDialog
          onClose={() => {
            setOpenCadentGasDialog(false);
          }}
          company={declarationCompany}
          setCompanies={setCompanies}
        />
      </Dialog>
    );
  };

  const renderEditCompanyDialog = () => {
    return (
      <EditCompanyDialog
        open={openEditDialog}
        onClose={() => {
          setOpenEditDialog(false);
        }}
        setCompanies={setCompanies}
        company={selectedEditCompany}
        setOpenDeleteDialog={setOpenDeleteDialog}
        companies={companies}
        debts={debts}
        setDebts={setDebts}
      />
    );
  };

  const renderInfoDialog = () => {
    return (
      <InfoDialog
        open={openInfoDialog}
        setOpenUUDeclarationDialog={setOpenUUDeclarationDialog}
        onClose={() => {
          setOpenInfoDialog(false);
        }}
        company={selectedEditCompany}
      />
    );
  };

  const renderDeleteCompanyDialog = () => {
    return (
      <DeleteCompanyDialog
        open={openDeleteDialog}
        onClose={() => {
          setOpenDeleteDialog(false);
        }}
        setCompanies={setCompanies}
        company={selectedEditCompany}
      />
    );
  };

  const renderUpdatedRecommendationsModal = () => {
    const isShareSecurely =
      updatedRecommendationsModalSource === "share_securely";
    const title = isShareSecurely
      ? "We’ve updated your share list"
      : "We’ve updated your recommendations";

    let description;
    if (isShareSecurely) {
      const companiesText =
        ineligibleCompanies.length > 1
          ? "one or more companies"
          : ineligibleCompanies[0];
      const meetCriteriaText =
        ineligibleCompanies.length > 1 ? "no longer meet" : "no longer meets";
      const removedText =
        ineligibleCompanies.length > 1
          ? "They’ve been removed"
          : "It’s been removed";

      description = (
        <>
          <Typography data-testid="pages-private-dashboard-render-updated-recommendations-modal-text-action-1" sx={{ fontSize: "1.1rem", color: "#4b5563", mb: 2 }}>
            Based on your latest information, <strong>{companiesText}</strong>{" "}
            {meetCriteriaText} the criteria to be recommended.
          </Typography>
          <Typography data-testid="pages-private-dashboard-render-updated-recommendations-modal-text-so-you-can-continue-with-the-right-options." sx={{ fontSize: "1.1rem", color: "#4b5563" }}>
            {removedText} so you can continue with the right options.
          </Typography>
        </>
      );
    } else {
      description = (
        <>
          <Typography data-testid="pages-private-dashboard-render-updated-recommendations-modal-text-action-2" sx={{ fontSize: "1.1rem", color: "#4b5563", mb: 2 }}>
            Based on your latest information, this company is no longer suitable
            for your situation.
          </Typography>
          <Typography data-testid="pages-private-dashboard-render-updated-recommendations-modal-text-action-3" sx={{ fontSize: "1.1rem", color: "#4b5563" }}>
            Don’t worry - you can explore other options or review your details.
          </Typography>
        </>
      );
    }

    return (
      <Dialog data-testid="pages-private-dashboard-render-updated-recommendations-modal-modal-action"
        open={showUpdatedRecommendationsModal}
        onClose={() => setShowUpdatedRecommendationsModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 2,
          },
        }}
      >
        <DialogTitle data-testid="pages-private-dashboard-render-updated-recommendations-modal-header-action"
          sx={{
            textAlign: "center",
            fontWeight: 700,
            fontSize: "1.5rem",
            color: "#00243e",
          }}
        >
          {title}
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center" }}>{description}</DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button data-testid="pages-private-dashboard-render-updated-recommendations-modal-btn-got-it"
            variant="contained"
            onClick={() => setShowUpdatedRecommendationsModal(false)}
            sx={{
              backgroundColor: ThemeColours.buttonVar1,
              borderRadius: 2,
              px: 4,
              py: 1,
              textTransform: "none",
              fontSize: "1rem",
              "&:hover": {
                backgroundColor: ThemeColours.buttonVar1,
                opacity: 0.9,
              },
            }}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderPlaceholder = (isMobile?: boolean | undefined) => {
    if (!hasCompaniesLoaded) {
      return (
        <Box data-testid="pages-private-dashboard-render-placeholder-container-action-1"
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "80%",
            marginTop: isMobile ? -3 : 10,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box data-testid="pages-private-dashboard-render-placeholder-container-action-2"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height={isMobile ? "auto" : "100%"}
        textAlign="center"
        marginTop={5}
        marginBottom={5}
      >
        <img
          className="placeholder-illustration"
          alt="Placeholder Illustration"
          src={placeholderIllustration}
          style={{ marginBottom: 16, height: 150 }}
        />

        <Typography data-testid="pages-private-dashboard-render-placeholder-text-you-haven’t-added-anycompanies-yet."
          sx={{
            color: "#013B3E",
            fontWeight: 700,
            fontSize: 24,
            lineHeight: 1,
          }}
        >
          You haven’t added any <br></br> companies yet.
        </Typography>
      </Box>
    );
  };

  // ==================== Main Render ====================

  return (
    <DashboardLayout>
      {/* Mobile View Content */}
      <Box data-testid="pages-private-dashboard-share-page-container-action-1" sx={{ display: { xs: "block", md: "none" }, pb: 14 }}>
        {renderHeader()}
        {renderTabs()}

        {hasNoCompanies && hasCompaniesLoaded && renderPlaceholder(true)}
        <Box data-testid="pages-private-dashboard-share-page-container-action-2" sx={{ mb: 4, scrollMarginTop: "100px" }}>
          {renderCompanySection(
            "All",
            groupedCompanies.all || [],
            allSectionRef,
            "all-section",
          )}
        </Box>
        {/* Sticky Bottom Navigation - Mobile */}
        <Paper data-testid="pages-private-dashboard-share-page-container-action-3"
          elevation={8}
          sx={{
            position: "fixed",
            bottom: "calc(89px + env(safe-area-inset-bottom))",
            left: 0,
            right: 0,
            zIndex: 999,
            borderTop: "1px solid",
            borderColor: "divider",
            backgroundColor: "white",
            px: 2,
            py: 1,
          }}
        >
          <Box data-testid="pages-private-dashboard-share-page-container-action-4"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              alignItems: "stretch",
            }}
          >
            <Box data-testid="pages-private-dashboard-share-page-container-action-5" sx={{ textAlign: "left" }}>
              <Typography data-testid="pages-private-dashboard-share-page-text-sharing-with-:-1"
                component="span"
                sx={{
                  color: "#013B3E",
                  fontWeight: 500,
                  fontSize: 16,
                  lineHeight: 1.2,
                }}
              >
                Sharing With :{" "}
                <Typography data-testid="pages-private-dashboard-share-page-text-action-1"
                  component="span"
                  sx={{
                    fontWeight: 700,
                    fontSize: 16,
                    lineHeight: 1.2,
                    color: "#013B3E",
                  }}
                >
                  {totalCompaniesCount}{" "}
                  {totalCompaniesCount === 1 ? "Company" : "Companies"}
                </Typography>
              </Typography>
            </Box>
            <Button data-testid="pages-private-dashboard-share-page-btn-action-1"
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              startIcon={<ShareIcon />}
              sx={{
                borderRadius: 1,
                textTransform: "none",
                fontWeight: 600,
                py: 1.5,
                boxShadow: "none",
              }}
              onClick={() => shareData()}
              disabled={isSharing}
            >
              {isSharing ? "Sharing..." : "Share Securely"}
            </Button>
          </Box>
        </Paper>
      </Box>

      {/* Desktop View Content */}
      <Box data-testid="pages-private-dashboard-share-page-container-action-6" sx={{ display: { xs: "none", md: "block" }, pb: 14 }}>
        {renderHeader()}
        {renderTabs()}

        {hasNoCompanies && renderPlaceholder()}

        <Box data-testid="pages-private-dashboard-share-page-container-action-7" sx={{ mb: 4, scrollMarginTop: "100px" }}>
          {renderCompanySection(
            "All",
            groupedCompanies.all || [],
            allSectionRef,
            "all-section",
          )}
        </Box>

        {/* Sticky Bottom Navigation - Desktop */}
        <Paper data-testid="pages-private-dashboard-share-page-container-action-8"
          elevation={8}
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            borderTop: "1px solid",
            borderColor: "divider",
            backgroundColor: "white",
            px: 2,
            py: 2,
          }}
        >
          <Box data-testid="pages-private-dashboard-share-page-container-action-9"
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              margin: "0 auto",
              px: 4,
              maxWidth: "1216px",
            }}
          >
            <Typography data-testid="pages-private-dashboard-share-page-text-sharing-with-:-2"
              component="span"
              sx={{
                fontWeight: 500,
                fontSize: 16,
                color: "#013B3E",
              }}
            >
              Sharing With :{" "}
              <Typography data-testid="pages-private-dashboard-share-page-text-action-2"
                component="span"
                sx={{
                  fontWeight: 700,
                  fontSize: 16,
                  color: "#013B3E",
                }}
              >
                {totalCompaniesCount}{" "}
                {totalCompaniesCount === 1 ? "Company" : "Companies"}
              </Typography>
            </Typography>
            <Button data-testid="pages-private-dashboard-share-page-btn-action-2"
              variant="contained"
              color="primary"
              size="large"
              startIcon={<ShareIcon />}
              sx={{
                borderRadius: 1,
                textTransform: "none",
                fontWeight: 600,
                py: 1.3,
                px: 3.5,
                boxShadow: "none",
              }}
              onClick={() => shareData()}
              disabled={isSharing}
            >
              {isSharing ? "Sharing..." : "Share Securely"}
            </Button>
          </Box>
        </Paper>
      </Box>

      {renderDeclarationModal()}
      {renderAddCompanyDialog()}
      {renderEditCompanyDialog()}
      {renderInfoDialog()}
      {renderDeleteCompanyDialog()}
      {renderUUDeclarationDialog()}
      {renderYWCTDeclarationDialog()}
      {renderCadentGasDialog()}
      {renderShareHistoryDialog()}

      <Dialog data-testid="pages-private-dashboard-share-page-modal-action"
        open={isAdditionalSupportOpen}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            ...(isMobile && {
              margin: 0,
              width: "100%",
              maxWidth: "100%",
              maxHeight: "90vh",
              height: "90vh",
              borderRadius: "16px 16px 0 0",
              position: "fixed",
              bottom: 0,
            }),
            ...(!isMobile && {
              height: "100vh",
            }),
          },
        }}
        TransitionComponent={Transition as any}
        BackdropComponent={Backdrop}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
          },
        }}
      >
        <AdditionalSupportModal
          onClose={() => {
            setIsAdditionalSupportOpen(false);
          }}
          freepostCreditorNamesList={freepostCreditorNamesData}
          setFreePostLastUpdateDate={setFreePostLastUpdateDate}
          digitalData={digitalData}
          ieMstrLenderData={ieMstrLenderData}
          digitalLenders={digitalLenders}
          summary={summary}
          setCompanies={setCompanies}
          companies={companies}
          setCompanyShareLastUpdateDate={setCompanyShareLastUpdateDate}
          onEligibleConditions={(conditions: string[]) => {
            setEligibleAspConditions(conditions);
          }}
        />
      </Dialog>
      {renderUpdatedRecommendationsModal()}
    </DashboardLayout>
  );
};

export default SharePage;
